import { canAddAffix, AFFIXES, COMPONENTS } from "../content/affixes";
import { eligibleSkillNodes, canAddSkillNode, skillNodeById } from "../content/skill-tree";
import { BIOMES, dangerLabel } from "../content/biomes";
import { ENEMY_ROLES } from "../content/enemies";
import { sampleHeight, sampleTerrain } from "../world/terrain";
import {
  ARENA_GUARDIAN_LABEL,
  BOSS_ARENA_BANNER,
  BOSS_ARENA_CLEAR_BANNER,
  BOSS_ARENA_HINT_RANGE_M,
  BOSS_ARENA_OBJECTIVE,
  CENTURION_BANNER,
  CENTURION_CLEAR_BANNER,
  CENTURION_OBJECTIVE,
  DREAD_HARVESTER_BANNER,
  DREAD_HARVESTER_CLEAR_BANNER,
  DREAD_HARVESTER_OBJECTIVE,
  FOUNDRY_LEVIATHAN_BANNER,
  FOUNDRY_LEVIATHAN_CLEAR_BANNER,
  FOUNDRY_LEVIATHAN_OBJECTIVE,
  NULL_CROWN_BANNER,
  NULL_CROWN_CLEAR_BANNER,
  NULL_CROWN_OBJECTIVE,
  SIEGE_WARDEN_BANNER,
  SIEGE_WARDEN_CLEAR_BANNER,
  SIEGE_WARDEN_OBJECTIVE,
  VELOCITY_SOVEREIGN_BANNER,
  VELOCITY_SOVEREIGN_CLEAR_BANNER,
  VELOCITY_SOVEREIGN_OBJECTIVE,
  poiDef,
  poiDisplayName,
} from "../content/pois";
import { frameWeaponDamageMultiplier, SHIP_FRAMES } from "../content/ships";
import type {
  EliteAffixId,
  EnemyRoleDef,
  EnemyRoleId,
  WeaponId,
  WeaponSlot,
} from "../content/schemas";
import { isWeaponModOffer, parseWeaponModOffer, weaponModById } from "../content/weapon-mods";
import {
  applyPayloadOnHit,
  applyWeaponModStack,
  eligibleWeaponModOffers,
  resolveSlotModEffects,
  scaleDamageForPayload,
  shrapnelDirections,
  shrapnelSpecFromKill,
  steerProjectileHoming,
  tickEnemyStatuses,
  type SlotModEffects,
} from "./weapon-mods";
import type { ChunkData } from "../world/chunks";
import { ChunkStreamer } from "../world/chunks";
import { materializePois, resetPoiIds } from "../world/pois";
import type { PlayerCommand } from "./commands";
import { createNeutralCommand, normalizeCommand } from "./commands";
import { applyCraft, toggleSelectedWeapon } from "./crafting";
import {
  applyWeaponCalibration,
  resolveWeaponCalibration,
  type WeaponCalibrationAxis,
} from "./weapon-calibration";
import {
  ACTIVE_CHUNK_RADIUS,
  AFFIX_DROP_CHANCE,
  BASE_SPEED_MPS,
  BOOST_MULTIPLIER,
  CARRIER_RADIUS_M,
  DIRECTOR_BASE_BUDGET,
  DIRECTOR_BLIND_SPOT_DEG,
  DIRECTOR_EARLY_WINDOW_S,
  DIRECTOR_ELITE_CHANCE_PER_LEVEL,
  DIRECTOR_GRACE_S,
  DIRECTOR_LEVEL_THREAT_PER_LEVEL,
  DIRECTOR_MAX_ACTIVE,
  DIRECTOR_MAX_ACTIVE_CAP,
  DIRECTOR_SPAWN_COOLDOWN_MIN_S,
  DIRECTOR_SPAWN_COOLDOWN_S,
  DIRECTOR_SPAWN_STAGGER_S,
  DRIFT_DAMPING,
  EXTRACT_HOLD_S,
  EXTRACT_RADIUS_M,
  JAMMER_RADIUS_M,
  JAMMER_REGEN_MULT,
  LEVEL_UP_INVULN_S,
  MAX_SKILL_NODES_PER_RUN,
  PICKUP_RADIUS_M,
  PLAYER_HOVER_HEIGHT_M,
  PLAYER_BASE_HULL,
  PLAYER_BASE_SHIELD,
  PLAYER_RADIUS_M,
  SHIELD_REGEN_DELAY_S,
  SHIELD_REGEN_PER_S,
  SKILL_PICK_CHOICES,
  TURRET_TELEGRAPH_EARLY_S,
  TURRET_TELEGRAPH_S,
  TURN_RATE_RAD_PS,
} from "./constants";
import { findSoftLockTarget, weaponRangeProxy, type SoftLockTarget } from "./combat-assist";
import { defaultAiState, leadAimDirection, updateEnemyMotion } from "./enemy-ai";
import { scaleCareerSalvage } from "./economy";
import {
  roleAllowedAtLevel,
  roleAllowedAtPilotRank,
  spawnTierForLevel,
  threatGradeForLevel,
  tierMultForLevel,
} from "./enemy-scaling";
import {
  angleOf,
  clamp,
  dist2,
  fromAngle,
  len2,
  normalize2,
  segmentCircleHitFraction,
  vec2,
} from "./math";
import { computeModifiers } from "./modifiers";
import type { BankedProfile, RunSave } from "./persistence";
import { createDefaultBankedProfile } from "./persistence";
import {
  activeSortieContract,
  contractBossObjectiveMet,
  contractRunSummary,
  queueSortieContract as purchaseSortieContract,
  type SortieContractId,
} from "./sortie-contracts";
import {
  BOSS_ARENA_XP,
  CENTURION_XP,
  DREAD_HARVESTER_XP,
  FOUNDRY_LEVIATHAN_XP,
  NULL_CROWN_XP,
  SIEGE_WARDEN_XP,
  VELOCITY_SOVEREIGN_XP,
  awardTimeSurvivedXp,
  cloneRunProgress,
  createRunProgress,
  grantXp,
  recordEnemyKill,
  resetKillStreak,
  xpForSalvagePickup,
  type RunProgress,
} from "./progression";
import {
  buildRunSummary,
  createComboState,
  createScoreBreakdown,
  isOverkill,
  isTacticianKill,
  processEnemyKill,
  resetComboOnDamage,
  scoreForSalvagePickup,
  scoreForTimeSurvived,
  tickCombo,
  type ComboState,
  type ScoreBreakdown,
  type ScoreFeedEntry,
  type ScorePopup,
  type RunSummary,
} from "./scoring";
import {
  cloneRunDebriefStats,
  createRunDebriefStats,
  noteDebriefDamage,
  noteDebriefKill,
  noteDebriefSalvage,
  noteDebriefWeaponShot,
  type RunDebriefStats,
} from "./run-debrief";
import {
  createIncursionState,
  incursionSpawnBatch,
  INCURSION_LABELS,
  rollIncursionWave,
  startIncursion,
  tickIncursion,
  type IncursionSpawnSpec,
  type IncursionState,
} from "./director-waves";
import { ELITE_AFFIXES, rollEliteAffix } from "../content/elite-affixes";
import {
  applyRankPoints,
  rankPointsForOutcome,
  recordPersonalBest,
  addBranchMastery,
  hasRerollUnlock,
  hasBanishUnlock,
  rerollCharges,
} from "./pilot-meta";
import {
  applyDailyOpEvent,
  claimDailyOp,
  ensureDailyOps,
  utcDateKey,
  type DailyOpEvent,
} from "./daily-ops";
import { applyExtractFailure, applyExtractSuccess } from "./extract-heat";
import {
  BOUNTY_SCORE,
  BOUNTY_XP,
  bountySnapshot,
  clearBounty,
  createBountyState,
  tickBounty,
  type BountyState,
} from "./bounty";
import { evaluateAchievements } from "../meta/achievement-engine";
import {
  loadoutWeaponForSlot,
  ONBOARDING_WEAPON,
  rollWeaponLoot,
  shortWeaponName,
  weaponRecycleSalvage,
  WEAPONS,
} from "../content/weapons";
import {
  buildLastDamageSource,
  formatDeathMessage,
  type DamageSourceInput,
  type LastDamageSource,
} from "./death-report";
import {
  ONBOARDING_SOFT_GATE_S,
  ONBOARDING_STEP,
  onboardingMessage,
  type CoachPulse,
} from "../content/onboarding";
import { pickupAnnounce } from "../content/pickup-identity";
import { createRng, hashSeed, pickWeighted, rngRange } from "./rng";
import type {
  AudioEvent,
  DropState,
  EnemyState,
  HazardState,
  HitVignetteState,
  ProjectileState,
  RunInventory,
  RunPhase,
  ShipState,
  TelegraphState,
  TelemetryEvent,
  WeaponHudSlot,
  WorldSnapshot,
} from "./snapshot";

type WeaponCooldownMap = Partial<Record<WeaponId, number>>;

const HARVESTER_VACUUM_RANGE_M = 34;
const HARVESTER_INTAKE_RADIUS_M = 3.2;
const HARVESTER_PULL_SPEED_MPS = 22;
const LEVIATHAN_SCRAP_SHIELD_RESTORE = 4;
const LEVIATHAN_DOCK_RADIUS_M = 4;
const SAPPER_BOOST_DRAIN = 0.22;
const NULLIFIER_FIELD_RADIUS_M = 28;
const LEECH_SHIELD_RETURN_MULT = 0.85;
const NULL_CROWN_NODE_SHIELD_REGEN_PER_S = 12;
const CENTURION_COUNTERPULSE_RADIUS_M = 45;
const CENTURION_COUNTERPULSE_BASE_SHIELD = 70;
const CENTURION_COUNTERPULSE_SHIELD_PER_ROUND = 8;
const TENDER_SUPPORT_RANGE_M = 26;
const TENDER_SHIELD_REGEN_PER_S = 14;

function bossPresentation(role: EnemyRoleId): {
  banner: string;
  objective: string;
  clearBanner: string;
  label: string;
} {
  if (role === "centurion") {
    return {
      banner: CENTURION_BANNER,
      objective: CENTURION_OBJECTIVE,
      clearBanner: CENTURION_CLEAR_BANNER,
      label: CENTURION_BANNER,
    };
  }
  if (role === "null_crown") {
    return {
      banner: NULL_CROWN_BANNER,
      objective: NULL_CROWN_OBJECTIVE,
      clearBanner: NULL_CROWN_CLEAR_BANNER,
      label: NULL_CROWN_BANNER,
    };
  }
  if (role === "leviathan") {
    return {
      banner: FOUNDRY_LEVIATHAN_BANNER,
      objective: FOUNDRY_LEVIATHAN_OBJECTIVE,
      clearBanner: FOUNDRY_LEVIATHAN_CLEAR_BANNER,
      label: FOUNDRY_LEVIATHAN_BANNER,
    };
  }
  if (role === "sovereign") {
    return {
      banner: VELOCITY_SOVEREIGN_BANNER,
      objective: VELOCITY_SOVEREIGN_OBJECTIVE,
      clearBanner: VELOCITY_SOVEREIGN_CLEAR_BANNER,
      label: VELOCITY_SOVEREIGN_BANNER,
    };
  }
  if (role === "harvester") {
    return {
      banner: DREAD_HARVESTER_BANNER,
      objective: DREAD_HARVESTER_OBJECTIVE,
      clearBanner: DREAD_HARVESTER_CLEAR_BANNER,
      label: DREAD_HARVESTER_BANNER,
    };
  }
  if (role === "warden") {
    return {
      banner: SIEGE_WARDEN_BANNER,
      objective: SIEGE_WARDEN_OBJECTIVE,
      clearBanner: SIEGE_WARDEN_CLEAR_BANNER,
      label: SIEGE_WARDEN_BANNER,
    };
  }
  return {
    banner: BOSS_ARENA_BANNER,
    objective: BOSS_ARENA_OBJECTIVE,
    clearBanner: BOSS_ARENA_CLEAR_BANNER,
    label: ARENA_GUARDIAN_LABEL,
  };
}

function emptyInventory(weapons: readonly WeaponId[]): RunInventory {
  const loadout = weapons.length > 0 ? [...weapons] : (["kinetic_scatter"] as WeaponId[]);
  return {
    salvage: 0,
    weapons: loadout,
    components: [],
    affixes: [],
    eliteCores: 0,
    keys: 0,
  };
}

function findVisualFeature(
  seed: number,
  sceneId:
    | "terrain-ridge"
    | "terrain-lake"
    | "poi-slope"
    | "combat-density"
    | "touch-combat"
    | "run-debrief",
): { x: number; y: number } {
  if (sceneId === "combat-density" || sceneId === "touch-combat" || sceneId === "run-debrief") {
    return { x: 720, y: 460 };
  }
  let best = { x: 900, y: 700, score: Number.NEGATIVE_INFINITY };
  for (let y = -2600; y <= 2600; y += 56) {
    for (let x = -2600; x <= 2600; x += 56) {
      if (Math.hypot(x, y) < 380) continue;
      const sample = sampleTerrain(seed, x, y);
      const broadRelief =
        Math.abs(sampleHeight(seed, x + 112, y) - sampleHeight(seed, x - 112, y)) +
        Math.abs(sampleHeight(seed, x, y + 112) - sampleHeight(seed, x, y - 112));
      const localRelief =
        Math.abs(sampleHeight(seed, x + 12, y) - sampleHeight(seed, x - 12, y)) +
        Math.abs(sampleHeight(seed, x, y + 12) - sampleHeight(seed, x, y - 12));
      const dry = sample.waterMask < 0.02 && sample.lake < 0.02;
      const score =
        sceneId === "terrain-lake"
          ? sample.waterMask * 100 + sample.lake * 20
          : dry && localRelief < 20
            ? broadRelief - localRelief * 1.8 + sample.height * 0.08
            : Number.NEGATIVE_INFINITY;
      if (score > best.score) best = { x, y, score };
    }
  }
  return { x: best.x, y: best.y + (sceneId === "terrain-lake" ? 70 : 55) };
}

export type SimulationConfig = {
  seed?: number;
  frameId?: string;
  trailId?: string;
  banked?: BankedProfile;
  onboarding?: boolean;
};

/**
 * Pure 60 Hz expedition simulation. No Three.js, no performance.now() in rules.
 */
export class ExpeditionSimulation {
  private tick = 0;
  private simTime = 0;
  private seed: number;
  private phase: RunPhase = "carrier";
  private nextId = 1;
  private player: ShipState;
  private projectiles: ProjectileState[] = [];
  private readonly counterpulseClearedProjectileIds = new Set<number>();
  private enemies: EnemyState[] = [];
  private hazards: HazardState[] = [];
  private telegraphs: TelegraphState[] = [];
  private drops: DropState[] = [];
  private pois: WorldSnapshot["pois"] = [];
  private carrierX = 0;
  private carrierY = 0;
  private extractProgress = 0;
  private runInventory: RunInventory;
  private onboardingStep = 0;
  /** Soft-gate timer after Q/F tip; advances even if unused. */
  private onboardingSoftGateTimer = 0;
  private primaryKillCount = 0;
  private firedSecondaryOrOrdnance = false;
  private pickedOnboardingCrate = false;
  private coachPulse: CoachPulse = null;
  private weaponCd: WeaponCooldownMap = {};
  private directorCd = DIRECTOR_GRACE_S;
  private spawnQueue: IncursionSpawnSpec[] = [];
  private spawnQueueCd = 0;
  private threatSpent = 0;
  private message = "Select launch when ready.";
  private audioEvents: AudioEvent[] = [];
  private telemetryEvents: TelemetryEvent[] = [];
  private streamer: ChunkStreamer;
  private eliteSpawned = false;
  /** POIs that already fired an entry tip / encounter (boss arenas stay uncleared until kill). */
  private readonly tippedPoiIds = new Set<number>();
  /** Boss arenas that have already spawned their Arena Guardian this run. */
  private readonly engagedBossPoiIds = new Set<number>();
  /** poiId → guardian enemy id for active boss encounters. */
  private readonly bossGuardianByPoi = new Map<number, number>();
  private bossBannerTimer = 0;
  private bossBannerLabel = "";
  private bossBannerTone: "enter" | "clear" = "enter";
  private bossObjective: string | null = null;
  private bossAtmosphere = 0;
  private runBossArenasCleared = 0;
  private relayLullTimer = 0;
  private cosmeticsTrailId: string;
  private settings: BankedProfile["settings"];
  private banked: BankedProfile;
  private activeContractId: SortieContractId | null = null;
  private launchTimer = 0;
  private paused = false;
  private messageBeforePause = "";
  private lastCommand = createNeutralCommand();
  private runProgress: RunProgress = createRunProgress();
  private hitVignette: HitVignetteState | null = null;
  private hitVignetteTimer = 0;
  private comboState: ComboState = createComboState();
  private scoreBreakdown: ScoreBreakdown = createScoreBreakdown();
  private scoreFeed: ScoreFeedEntry[] = [];
  private scorePopups: ScorePopup[] = [];
  private killsByRole: Partial<Record<EnemyRoleId, number>> = {};
  private runSalvageCollected = 0;
  private debriefStats: RunDebriefStats = createRunDebriefStats();
  private runSummary: RunSummary | null = null;
  private nextScoreEventId = 1;
  private nextScorePopupId = 1;
  private incursion: IncursionState = createIncursionState(1);
  private aceSpawned = false;
  private directorPressure = 1;
  private adaptiveTimer = 0;
  private recentKills = 0;
  private recentHullDamage = 0;
  private runAceKilled = false;
  private wasBoosting = false;
  private boostCycleProcCd = 0;
  private shieldBreakCycleProcCd = 0;
  private assistTarget: SoftLockTarget | null = null;
  private prevAssistTargetId: number | null = null;
  private assistLockCd = 0;
  private softLockPulseTimer = 0;
  private lastDamageSource: LastDamageSource | null = null;
  private runIncursionSurvived = false;
  private runTacticianCount = 0;
  private runHadTripleKill = false;
  private runHadDoubleKill = false;
  private noHullDamageS = 0;
  private longestNoHullDamageS = 0;
  private bounty: BountyState = createBountyState(0, 1);
  private pickupFlash = 0;
  private pickupFlashAffixId: string | null = null;
  private pickupFlashKind: DropState["kind"] | null = null;

  public constructor(config: SimulationConfig = {}) {
    this.banked = config.banked ?? createDefaultBankedProfile();
    this.settings = { ...this.banked.settings };
    this.seed = config.seed ?? (Math.floor(Math.random() * 1_000_000_000) || 1);
    this.cosmeticsTrailId = config.trailId ?? this.banked.selectedTrailId;
    const frameId = config.frameId ?? this.banked.selectedFrameId;
    this.runInventory = emptyInventory(this.banked.selectedWeapons);
    this.player = this.createPlayer(frameId);
    this.streamer = new ChunkStreamer(this.seed);
    this.incursion = createIncursionState(this.seed);
    this.bounty = createBountyState(0, this.seed);
    this.refreshDailyOps();
    resetPoiIds();
    if (config.onboarding ?? this.banked.runsCompleted === 0) {
      this.resetOnboardingRuntime();
      this.onboardingStep = ONBOARDING_STEP.MOVE;
      this.message = onboardingMessage(ONBOARDING_STEP.MOVE);
      // First run: skip carrier dwell so the pilot is airborne immediately.
      this.phase = "launching";
      this.launchTimer = 0.05;
    }
  }

  public getBanked(): BankedProfile {
    return this.banked;
  }

  public refreshDailyOps(nowMs = Date.now()): void {
    this.banked.dailyOps = ensureDailyOps(this.banked.dailyOps, utcDateKey(nowMs));
  }

  public applyBanked(profile: BankedProfile): void {
    this.banked = profile;
    this.settings = { ...profile.settings };
  }

  public setSetting<K extends keyof BankedProfile["settings"]>(
    key: K,
    value: BankedProfile["settings"][K],
  ): void {
    this.settings[key] = value;
    this.banked.settings[key] = value;
  }

  public selectFrame(frameId: string): void {
    if (!this.inBay()) return;
    if (!this.banked.unlockedFrames.includes(frameId)) {
      this.message = "Frame locked — craft it in the carrier bay.";
      return;
    }
    this.banked.selectedFrameId = frameId;
    this.player = this.createPlayer(frameId);
    this.message = `Frame selected: ${frameId}`;
    this.telemetry("frame_select", { frameId });
  }

  public selectTrail(trailId: string): void {
    if (!this.banked.cosmetics.includes(trailId)) {
      this.message = "Trail locked — craft the cosmetic kit.";
      return;
    }
    this.cosmeticsTrailId = trailId;
    this.banked.selectedTrailId = trailId;
    this.message = `Trail equipped: ${trailId}`;
  }

  public toggleWeapon(weaponId: WeaponId): void {
    if (!this.inBay()) {
      this.message = "Change loadout at the carrier.";
      return;
    }
    if (!this.banked.unlockedWeapons.includes(weaponId)) {
      this.message = "Weapon locked — craft the rack first.";
      return;
    }
    this.banked = toggleSelectedWeapon(this.banked, weaponId);
    this.runInventory = emptyInventory(this.banked.selectedWeapons);
    this.message = `Loadout: ${this.banked.selectedWeapons.join(", ")}`;
    this.telemetry("loadout_weapon", { weaponId });
  }

  /** Spend banked salvage to unlock frames/weapons/cosmetics/blueprints. */
  public craft(recipeId: string): boolean {
    if (!this.inBay() && this.phase !== "dead") {
      this.message = "Craft only while docked at the carrier.";
      return false;
    }
    const result = applyCraft(this.banked, recipeId);
    if (!result.ok) {
      this.message = result.message;
      return false;
    }
    this.banked = result.profile;
    this.message = result.message;
    this.pushAudio({ type: "pickup", kind: "craft" });
    this.telemetry("craft", { recipeId });
    return true;
  }

  /** Permanently tune an unlocked weapon while docked. */
  public calibrateWeapon(weaponId: WeaponId, axis: WeaponCalibrationAxis): boolean {
    if (!this.inBay() && this.phase !== "dead") {
      this.message = "Calibrate only while docked at the carrier.";
      return false;
    }
    const result = applyWeaponCalibration(this.banked, weaponId, axis);
    if (!result.ok) {
      this.message = result.message;
      return false;
    }
    this.banked = result.profile;
    this.message = result.message;
    this.pushAudio({ type: "pickup", kind: "craft" });
    this.telemetry("weapon_calibration", { weaponId, axis });
    return true;
  }

  /** Spend scrap to arm a repeatable challenge for the next expedition. */
  public queueSortieContract(contractId: SortieContractId): boolean {
    if (!this.inBay()) {
      this.message = "Arm sortie contracts only while docked at the carrier.";
      return false;
    }
    const result = purchaseSortieContract(this.banked, contractId);
    if (!result.ok) {
      this.message = result.message;
      return false;
    }
    this.banked = result.profile;
    this.message = result.message;
    this.pushAudio({ type: "pickup", kind: "craft" });
    this.telemetry("sortie_contract_armed", { contractId });
    return true;
  }

  public requestLaunch(): void {
    if (this.phase === "carrier" || this.phase === "extracted" || this.phase === "dead") {
      this.beginLaunch();
    }
  }

  private inBay(): boolean {
    return this.phase === "carrier" || this.phase === "extracted" || this.phase === "dead";
  }

  public pickSkillNode(nodeId: string): boolean {
    return this.applySkillPick(nodeId);
  }

  public pickSkillByIndex(index: number): boolean {
    const nodeId = this.runProgress.skillPickOffers[index];
    if (!nodeId) return false;
    return this.applySkillPick(nodeId);
  }

  /** Repair stale offers and roll new choices when a pick is still owed. */
  public ensureSkillPickOffers(): void {
    if (this.runSummary || this.phase === "dead" || this.phase === "extracted") {
      this.forfeitSkillPick();
      return;
    }
    this.repairSkillPickOffers();
    if (this.runProgress.unspentSkillPoints > 0 && this.runProgress.skillPickOffers.length === 0) {
      this.queueSkillPickIfNeeded();
    }
  }

  /** Drop pending pick without installing (skip / death / debrief). */
  public forfeitSkillPick(): void {
    this.runProgress.skillPickOffers = [];
    this.runProgress.levelUpPending = false;
    this.runProgress.unspentSkillPoints = 0;
  }

  /** Install the first offered skill — mobile fallback when UI is stuck. */
  public skipSkillPick(): boolean {
    const first = this.runProgress.skillPickOffers[0];
    if (!first) {
      this.forfeitSkillPick();
      return false;
    }
    return this.applySkillPick(first);
  }

  public getRunProgress() {
    return cloneRunProgress(this.runProgress);
  }

  public captureSnapshot(): WorldSnapshot {
    return this.snapshot();
  }

  /** Build-gated deterministic presentation fixture; never called by normal gameplay. */
  public applyVisualFixture(
    sceneId:
      | "terrain-ridge"
      | "terrain-lake"
      | "poi-slope"
      | "combat-density"
      | "touch-combat"
      | "run-debrief",
  ): WorldSnapshot {
    this.tick = 240;
    this.simTime = 4;
    this.phase = "expedition";
    this.paused = false;
    this.launchTimer = 0;
    this.runSummary = null;
    this.runProgress.skillPickOffers = [];
    this.runProgress.unspentSkillPoints = 0;
    this.runProgress.levelUpPending = false;
    this.enemies = [];
    this.projectiles = [];
    this.hazards = [];
    this.telegraphs = [];
    this.drops = [];
    this.pois = [];
    this.directorCd = 999;
    this.spawnQueue = [];
    this.message = "";
    this.bossObjective = null;
    this.incursion = createIncursionState(this.seed);

    const feature = findVisualFeature(this.seed, sceneId);
    this.player.x = feature.x;
    this.player.y = feature.y;
    this.player.vx = 0;
    this.player.vy = 0;
    this.player.vz = 0;
    this.player.facing = 0;
    this.player.altitude = sampleHeight(this.seed, feature.x, feature.y) + PLAYER_HOVER_HEIGHT_M;
    this.updateWorldStreaming();

    if (sceneId === "run-debrief") {
      this.phase = "dead";
      this.simTime = 487;
      this.player.hull = 0;
      this.runProgress.level = 9;
      this.runProgress.score = 24_560;
      this.runProgress.bestKillStreak = 14;
      this.runProgress.bestChain = 19;
      this.runProgress.takenNodes = [
        "reinforced_plating",
        "ablative_armor",
        "capacitor_expansion",
        "fast_cycle",
        "hot_rounds",
        "emergency_seal",
      ];
      this.scoreBreakdown = {
        kills: 13_480,
        chain: 4_150,
        multiKill: 2_400,
        style: 1_720,
        salvage: 1_860,
        time: 950,
      };
      this.killsByRole = {
        scout: 24,
        skirmisher: 14,
        turret: 8,
        bomber: 6,
        jammer: 5,
        breacher: 7,
        carrier: 3,
        ace: 2,
      };
      this.runSalvageCollected = 238;
      this.debriefStats = {
        damageTaken: { shield: 284, armor: 96, hull: 147 },
        killTimes: [
          18, 26, 31, 47, 62, 78, 83, 101, 126, 141, 167, 181, 194, 212, 226, 244, 268, 287, 301,
          319, 345, 362, 381, 397, 412, 431, 449, 463, 478,
        ],
        salvageSamples: [
          { t: 38, total: 12 },
          { t: 97, total: 46 },
          { t: 166, total: 83 },
          { t: 248, total: 121 },
          { t: 338, total: 174 },
          { t: 426, total: 214 },
          { t: 481, total: 238 },
        ],
        altitudeTime: { nap: 0, combat: 487, high: 0 },
        weaponShots: { kinetic_scatter: 412, arc_lance: 96, breach_torpedo: 31 },
        peakComboMultiplier: 2.85,
      };
      const deathReport: LastDamageSource = {
        enemyRole: "breacher",
        enemyName: "Elite Breacher",
        elite: true,
        isAce: false,
        attackKind: "melee",
        attackLabel: "ram",
        distanceM: 2.4,
        damage: 42,
        shieldAbsorbed: 0,
        armorAbsorbed: 8,
        hullDamage: 34,
      };
      this.lastDamageSource = deathReport;
      this.runSummary = buildRunSummary(
        "dead",
        this.runProgress,
        this.scoreBreakdown,
        this.comboState,
        this.killsByRole,
        this.simTime,
        this.runSalvageCollected,
        { deathReport, debrief: this.debriefStats },
      );
      this.message = "RUN DEBRIEF";
      return this.snapshot();
    }

    if (sceneId === "terrain-ridge" || sceneId === "terrain-lake") {
      this.pois = [];
      this.message =
        sceneId === "terrain-ridge" ? "RIDGE / VALLEY STUDY" : "BASIN / SHORELINE STUDY";
    }

    if (sceneId === "poi-slope") {
      const baseX = this.player.x;
      const baseY = this.player.y - 34;
      this.pois = [
        {
          id: this.nextId++,
          kind: "relay",
          x: baseX - 38,
          y: baseY,
          biome: "ash_band",
          cleared: false,
          radius: 22,
        },
        {
          id: this.nextId++,
          kind: "cache",
          x: baseX + 2,
          y: baseY - 12,
          biome: "ash_band",
          cleared: false,
          radius: 19,
        },
        {
          id: this.nextId++,
          kind: "ambush",
          x: baseX + 42,
          y: baseY + 4,
          biome: "ash_band",
          cleared: false,
          radius: 24,
        },
      ];
      this.message = "TACTICAL BOUNDARIES";
    }

    if (sceneId === "combat-density" || sceneId === "touch-combat") {
      this.runInventory = {
        salvage: 184,
        eliteCores: 3,
        keys: 1,
        weapons: ["kinetic_scatter", "arc_lance", "breach_torpedo"],
        components: ["plate_kit", "boost_cell"],
        affixes: ["hot_barrels", "nap_predator"],
      };
      this.runProgress.level = 8;
      this.runProgress.score = 18450;
      this.runProgress.xp = 92;
      this.runProgress.xpToNext = 160;
      this.runProgress.killStreak = 6;
      this.comboState.chainCount = 5;
      this.comboState.comboMultiplier = 1.8;
      this.player.shield = this.player.maxShield * 0.62;
      this.player.hull = this.player.maxHull * 0.78;
      const roles: EnemyRoleId[] = [
        "scout",
        "skirmisher",
        "turret",
        "bomber",
        "jammer",
        "breacher",
      ];
      roles.forEach((role, index) => {
        const angle = -1.15 + index * 0.42;
        this.spawnEnemy(role, index === 3, {
          x: this.player.x + Math.cos(angle) * (30 + index * 4),
          y: this.player.y - 22 + Math.sin(angle) * (28 + index * 3),
        });
      });
      this.hazards.push({
        id: this.nextId++,
        kind: "mine",
        x: this.player.x - 16,
        y: this.player.y - 18,
        altitude: sampleHeight(this.seed, this.player.x - 16, this.player.y - 18) + 1,
        life: 0.8,
        radius: 5,
        damage: 36,
      });
      this.incursion.active = true;
      this.incursion.activeTimer = 8;
      this.incursion.waveId = "elite_heavy";
      this.bossObjective = "BREAK THE ELITE FORMATION";
      this.message = "Payload acquired";
      this.carrierX = this.player.x + 180;
      this.carrierY = this.player.y - 120;
      if (sceneId === "touch-combat") this.settings.showTouchControls = true;
    }

    return this.snapshot();
  }

  public step(command: PlayerCommand): WorldSnapshot {
    this.audioEvents = [];
    this.telemetryEvents = [];
    this.coachPulse = null;
    const cmd = normalizeCommand(command);
    this.lastCommand = cmd;

    if (cmd.buttons.pause) {
      this.paused = !this.paused;
      if (this.paused) {
        this.messageBeforePause = this.message;
        this.message = "Paused";
      } else {
        this.message = this.messageBeforePause || this.message;
      }
    }
    if (cmd.buttons.restart) {
      this.relaunch();
      return this.snapshot();
    }
    if (this.paused) return this.snapshot();

    if (this.runProgress.skillPickOffers.length > 0) {
      if (this.player.hull <= 0 || this.phase === "dead") {
        this.forfeitSkillPick();
        if (this.player.hull <= 0) this.killPlayer();
        return this.snapshot();
      }
      this.tick += 1;
      if (cmd.skillPick >= 0 && cmd.skillPick < this.runProgress.skillPickOffers.length) {
        this.applySkillPick(this.runProgress.skillPickOffers[cmd.skillPick]!);
      }
      return this.snapshot();
    }

    this.tick += 1;
    this.simTime += 1 / 60;
    const dt = 1 / 60;

    if (
      (this.phase === "carrier" || this.phase === "onboarding" || this.phase === "extracted") &&
      cmd.buttons.interact
    ) {
      this.beginLaunch();
    }
    if (this.phase === "launching") {
      this.launchTimer -= dt;
      if (this.launchTimer <= 0) {
        this.phase =
          this.onboardingStep < ONBOARDING_STEP.DONE && this.banked.runsCompleted === 0
            ? "onboarding"
            : "expedition";
        const contract = activeSortieContract(this.activeContractId);
        this.message =
          this.phase === "onboarding"
            ? onboardingMessage(this.onboardingStep)
            : contract
              ? `${contract.name.toUpperCase()} — ${contractRunSummary(contract)}.`
              : "Expedition live. Your CARRIER is the lit pad — extract there with salvage.";
        this.pushAudio({ type: "launch" });
        this.telemetry("launch", { seed: this.seed });
      }
    }

    if (
      this.phase === "expedition" ||
      this.phase === "onboarding" ||
      this.phase === "extracting" ||
      this.phase === "launching"
    ) {
      this.updateFlight(cmd, dt);
      this.updateWeapons(cmd, dt);
      this.updateDirector(dt);
      this.updateIncursion(dt);
      this.updateAdaptiveDirector(dt);
      this.updateBounty(dt);
      this.updateEnemies(dt);
      this.pushApproachPresence();
      this.updateHazards(dt);
      this.updateProjectiles(dt);
      this.updateDrops(dt);
      this.updateWorldStreaming();
      this.updatePoiInteractions();
      this.updateBossEncounter(dt);
      this.updateExtract(dt);
      this.updateOnboarding(dt);
      this.regenShields(dt);
      this.updateProgression(dt);
      this.updateHitVignette(dt);
      this.tickAssistFeedback(dt);
      if (this.pickupFlash > 0) {
        this.pickupFlash = Math.max(0, this.pickupFlash - dt * 2.2);
        if (this.pickupFlash <= 0) {
          this.pickupFlashAffixId = null;
          this.pickupFlashKind = null;
        }
      }
      if (this.relayLullTimer > 0) this.relayLullTimer = Math.max(0, this.relayLullTimer - dt);
    }

    return this.snapshot();
  }

  public serializeRun(): RunSave {
    return {
      version: 2,
      seed: this.seed,
      tick: this.tick,
      simTime: this.simTime,
      phase: this.phase === "launching" ? "expedition" : this.phase,
      frameId: this.player.frameId,
      player: { ...this.player },
      runInventory: {
        ...this.runInventory,
        weapons: [...this.runInventory.weapons],
        components: [...this.runInventory.components],
        affixes: [...this.runInventory.affixes],
      },
      runProgress: cloneRunProgress(this.runProgress),
      comboState: { ...this.comboState, recentTags: [...this.comboState.recentTags] },
      scoreBreakdown: { ...this.scoreBreakdown },
      killsByRole: { ...this.killsByRole },
      runSalvageCollected: this.runSalvageCollected,
      debriefStats: cloneRunDebriefStats(this.debriefStats),
      carrierX: this.carrierX,
      carrierY: this.carrierY,
      onboardingStep: this.onboardingStep,
      extractProgress: this.extractProgress,
      enemyCount: this.enemies.length,
      eliteAlive: this.enemies.some((e) => e.elite),
      cosmeticsTrailId: this.cosmeticsTrailId,
      activeSortieContract: this.activeContractId,
      bossArenasCleared: this.runBossArenasCleared,
    };
  }

  public restoreRun(save: RunSave): void {
    this.seed = save.seed;
    this.tick = save.tick;
    this.simTime = save.simTime;
    this.phase = save.phase === "dead" || save.phase === "extracted" ? "carrier" : save.phase;
    this.player = { ...save.player };
    this.runInventory = {
      ...save.runInventory,
      weapons: [...save.runInventory.weapons],
      components: [...save.runInventory.components],
      affixes: [...save.runInventory.affixes],
    };
    this.carrierX = save.carrierX;
    this.carrierY = save.carrierY;
    this.onboardingStep = save.onboardingStep;
    this.extractProgress = save.extractProgress;
    this.cosmeticsTrailId = save.cosmeticsTrailId;
    this.activeContractId = save.activeSortieContract ?? null;
    this.runBossArenasCleared = Math.max(0, save.bossArenasCleared ?? 0);
    this.runProgress = save.runProgress ? cloneRunProgress(save.runProgress) : createRunProgress();
    this.comboState = save.comboState
      ? { ...save.comboState, recentTags: [...save.comboState.recentTags] }
      : createComboState();
    this.scoreBreakdown = save.scoreBreakdown ? { ...save.scoreBreakdown } : createScoreBreakdown();
    this.killsByRole = save.killsByRole ? { ...save.killsByRole } : {};
    this.runSalvageCollected = save.runSalvageCollected ?? 0;
    this.debriefStats = save.debriefStats
      ? cloneRunDebriefStats(save.debriefStats)
      : createRunDebriefStats();
    this.scoreFeed = [];
    this.scorePopups = [];
    this.runSummary = null;
    this.streamer = new ChunkStreamer(this.seed);
    this.projectiles = [];
    this.enemies = [];
    this.hazards = [];
    this.telegraphs = [];
    this.drops = [];
    this.pois = [];
    resetPoiIds();
    this.updateWorldStreaming();
    this.eliteSpawned = save.eliteAlive || save.onboardingStep >= ONBOARDING_STEP.CARRIER_HOME;
    this.tippedPoiIds.clear();
    this.engagedBossPoiIds.clear();
    this.bossGuardianByPoi.clear();
    this.bossBannerTimer = 0;
    this.bossBannerLabel = "";
    this.bossBannerTone = "enter";
    this.bossObjective = null;
    this.bossAtmosphere = 0;
    this.message = "Run resumed.";
    this.telemetry("run_resume", { tick: this.tick });
    this.repairSkillPickOffers();
  }

  /** Drop stale saved offers and reroll if the player still owes a skill pick. */
  private repairSkillPickOffers(): void {
    if (this.runProgress.unspentSkillPoints <= 0) {
      this.runProgress.skillPickOffers = [];
      this.runProgress.levelUpPending = false;
      return;
    }
    const valid = this.runProgress.skillPickOffers.filter(
      (id) => id && (skillNodeById(id) || parseWeaponModOffer(id)),
    );
    if (valid.length === this.runProgress.skillPickOffers.length && valid.length > 0) return;
    this.runProgress.skillPickOffers = [];
    this.queueSkillPickIfNeeded();
  }

  private equippedWeaponSlots(): WeaponSlot[] {
    const slots: WeaponSlot[] = [];
    for (const slot of ["primary", "secondary", "ordnance"] as const) {
      if (loadoutWeaponForSlot(this.runInventory.weapons, slot)) slots.push(slot);
    }
    return slots;
  }

  private mods() {
    return computeModifiers(this.runInventory, this.runProgress.takenNodes);
  }

  private createPlayer(frameId: string) {
    const frame = SHIP_FRAMES[frameId] ?? SHIP_FRAMES.skimmer!;
    const startAltitude =
      sampleHeight(this.seed, this.carrierX, this.carrierY) + PLAYER_HOVER_HEIGHT_M;
    const mods = this.mods();
    const maxHull = frame.hull + mods.hullBonus;
    const maxShield = (frame.shield + mods.shieldBonus) * mods.shieldMult;
    const maxArmor = Math.max(0, mods.armorBonus);
    return {
      id: this.nextId++,
      x: this.carrierX,
      y: this.carrierY,
      altitude: startAltitude,
      vx: 0,
      vy: 0,
      vz: 0,
      facing: 0,
      hull: maxHull,
      maxHull,
      shield: maxShield,
      maxShield,
      armor: maxArmor,
      maxArmor,
      boostFuel: 1,
      frameId: frame.id,
      invulnTimer: 0,
      shieldDelay: 0,
    };
  }

  private beginLaunch(): void {
    if (this.phase === "extracted" || this.phase === "dead" || this.phase === "carrier") {
      this.resetRunEntities();
    }
    this.runInventory = emptyInventory(this.banked.selectedWeapons);
    this.runProgress = createRunProgress();
    this.player = this.createPlayer(this.banked.selectedFrameId);
    this.phase = "launching";
    this.launchTimer = 0.6;
    this.extractProgress = 0;
    this.player.x = this.carrierX;
    this.player.y = this.carrierY;
    this.player.altitude =
      sampleHeight(this.seed, this.carrierX, this.carrierY) + PLAYER_HOVER_HEIGHT_M;
    this.player.invulnTimer = 1.2;
    this.activeContractId = this.banked.queuedSortieContract;
    this.banked = { ...this.banked, queuedSortieContract: null };
    const contract = activeSortieContract(this.activeContractId);
    if (contract && contract.incursionIntervalMult !== 1) {
      this.incursion.nextAt =
        this.simTime + (this.incursion.nextAt - this.simTime) * contract.incursionIntervalMult;
    }
    this.message = contract
      ? `${contract.name.toUpperCase()} ACTIVE — ${contractRunSummary(contract)}.`
      : "Launching…";
    // Fresh first-minute script each incomplete first-run launch.
    if (this.banked.runsCompleted === 0 && this.onboardingStep < ONBOARDING_STEP.DONE) {
      this.onboardingStep = ONBOARDING_STEP.MOVE;
      this.resetOnboardingRuntime();
    }
  }

  private resetOnboardingRuntime(): void {
    this.onboardingSoftGateTimer = 0;
    this.primaryKillCount = 0;
    this.firedSecondaryOrOrdnance = false;
    this.pickedOnboardingCrate = false;
    this.coachPulse = null;
  }

  public relaunch(): void {
    this.resetRunEntities();
    this.phase = "carrier";
    this.runInventory = emptyInventory(this.banked.selectedWeapons);
    this.runProgress = createRunProgress();
    this.player = this.createPlayer(this.banked.selectedFrameId);
    this.seed = hashSeed(this.seed, this.tick + 17) || this.seed + 1;
    this.streamer = new ChunkStreamer(this.seed);
    this.pois = [];
    resetPoiIds();
    this.message = "Carrier bay open. Craft unlocks, set loadout, then launch.";
    this.paused = false;
  }

  private resetRunEntities(): void {
    this.projectiles = [];
    this.weaponCd = {};
    this.enemies = [];
    this.hazards = [];
    this.telegraphs = [];
    this.drops = [];
    this.extractProgress = 0;
    this.threatSpent = 0;
    this.directorCd = DIRECTOR_GRACE_S;
    this.spawnQueue = [];
    this.spawnQueueCd = 0;
    this.eliteSpawned = false;
    this.tippedPoiIds.clear();
    this.engagedBossPoiIds.clear();
    this.bossGuardianByPoi.clear();
    this.bossBannerTimer = 0;
    this.bossBannerLabel = "";
    this.bossBannerTone = "enter";
    this.bossObjective = null;
    this.bossAtmosphere = 0;
    this.runBossArenasCleared = 0;
    this.relayLullTimer = 0;
    this.hitVignette = null;
    this.hitVignetteTimer = 0;
    this.comboState = createComboState();
    this.scoreBreakdown = createScoreBreakdown();
    this.scoreFeed = [];
    this.scorePopups = [];
    this.killsByRole = {};
    this.runSalvageCollected = 0;
    this.debriefStats = createRunDebriefStats();
    this.runSummary = null;
    this.activeContractId = null;
    this.nextScoreEventId = 1;
    this.nextScorePopupId = 1;
    this.incursion = createIncursionState(this.seed);
    this.aceSpawned = false;
    this.directorPressure = 1;
    this.adaptiveTimer = 0;
    this.recentKills = 0;
    this.recentHullDamage = 0;
    this.runAceKilled = false;
    this.runIncursionSurvived = false;
    this.runTacticianCount = 0;
    this.runHadTripleKill = false;
    this.runHadDoubleKill = false;
    this.noHullDamageS = 0;
    this.longestNoHullDamageS = 0;
    this.bounty = createBountyState(0, this.seed);
    this.wasBoosting = false;
    this.boostCycleProcCd = 0;
    this.shieldBreakCycleProcCd = 0;
  }

  private updateFlight(cmd: PlayerCommand, dt: number): void {
    const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
    const mods = this.mods();
    const boosting = cmd.buttons.boost && this.player.boostFuel > 0.05;
    this.boostCycleProcCd = Math.max(0, this.boostCycleProcCd - dt);
    this.shieldBreakCycleProcCd = Math.max(0, this.shieldBreakCycleProcCd - dt);
    const speed =
      BASE_SPEED_MPS * frame.speedMult * mods.speedMult * (boosting ? BOOST_MULTIPLIER : 1);
    const move = normalize2(vec2(cmd.moveX, cmd.moveY));
    const targetVx = move.x * speed;
    const targetVy = move.y * speed;
    const damp = 1 - Math.exp(-DRIFT_DAMPING * dt);
    this.player.vx += (targetVx - this.player.vx) * damp;
    this.player.vy += (targetVy - this.player.vy) * damp;
    this.player.x += this.player.vx * dt;
    this.player.y += this.player.vy * dt;

    const ground = sampleHeight(this.seed, this.player.x, this.player.y);
    this.player.altitude = ground + PLAYER_HOVER_HEIGHT_M;
    this.player.vz = 0;

    const aim = vec2(cmd.aimX, cmd.aimY);
    if (len2(aim) > 0.2) {
      const desired = Math.atan2(aim.y, aim.x);
      let delta = desired - this.player.facing;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      this.player.facing += clamp(delta, -TURN_RATE_RAD_PS * dt, TURN_RATE_RAD_PS * dt);
    } else if (len2(move) > 0.2) {
      this.player.facing = Math.atan2(move.y, move.x);
    }

    if (boosting) {
      this.player.boostFuel = Math.max(
        0,
        this.player.boostFuel - dt * 0.35 * (frame.boostFuelDrainMult ?? 1),
      );
      if ((frame.boostShieldRegenPerSecond ?? 0) > 0) {
        this.player.shield = Math.min(
          this.player.maxShield,
          this.player.shield + dt * (frame.boostShieldRegenPerSecond ?? 0),
        );
      }
      if (!this.wasBoosting) {
        this.pushAudio({ type: "boost" });
        const reduction = frame.boostCycleReduction ?? 0;
        if (reduction > 0 && this.boostCycleProcCd <= 0) {
          for (const weaponId of Object.keys(this.weaponCd) as WeaponId[]) {
            this.weaponCd[weaponId] = Math.max(0, (this.weaponCd[weaponId] ?? 0) * (1 - reduction));
          }
          this.boostCycleProcCd = frame.boostCycleProcCooldownS ?? 0;
        }
      }
    } else {
      const regen = this.player.frameId === "bulwark" ? 0.12 : 0.22;
      this.player.boostFuel = Math.min(1 + mods.boostBonus, this.player.boostFuel + dt * regen);
    }
    this.wasBoosting = boosting;

    const thrust = len2(move);
    const thrustIntensity = Math.min(
      1,
      Math.max(thrust, boosting ? 0.72 : 0) * (boosting ? 1.55 : 1),
    );
    if (thrustIntensity > 0.1) this.pushAudio({ type: "thrust", intensity: thrustIntensity });
    if (this.player.invulnTimer > 0) this.player.invulnTimer -= dt;
  }

  private slotModEffects(slot: WeaponSlot): SlotModEffects {
    return resolveSlotModEffects(this.runProgress.weaponMods, slot);
  }

  private updateWeapons(cmd: PlayerCommand, dt: number): void {
    for (const key of Object.keys(this.weaponCd) as WeaponId[]) {
      this.weaponCd[key] = Math.max(0, (this.weaponCd[key] ?? 0) - dt);
    }
    const mods = this.mods();
    this.refreshSoftLock();
    const tryFire = (weaponId: WeaponId, pressed: boolean) => {
      if (!pressed) return;
      if (!this.runInventory.weapons.includes(weaponId)) return;
      const def = WEAPONS[weaponId];
      if (!def) return;
      const cd = this.weaponCd[weaponId] ?? 0;
      if (cd > 0) return;
      const slotFx = this.slotModEffects(def.slot);
      const calibration = resolveWeaponCalibration(this.banked.weaponCalibration, weaponId);
      const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
      this.weaponCd[weaponId] =
        def.cooldown *
        mods.cooldownMult *
        slotFx.cooldownMult *
        calibration.cooldownMult *
        (frame.weaponCooldownMult ?? 1);
      if (def.slot === "secondary" || def.slot === "ordnance") {
        this.firedSecondaryOrOrdnance = true;
      }
      const facing = fromAngle(this.player.facing);
      const projectileSpeed = def.projectileSpeed * calibration.projectileSpeedMult;
      const range = weaponRangeProxy(projectileSpeed, def.life);
      const lock = findSoftLockTarget(
        {
          x: this.player.x,
          y: this.player.y,
          facing: this.player.facing,
          rangeProxy: range,
        },
        this.enemies,
      );
      void lock;
      const count = Math.max(1, def.count + slotFx.countDelta);
      const spreadBase = Math.max(0, def.spread + slotFx.spreadDelta);
      const damage =
        def.damage *
        mods.damageMult *
        slotFx.damageMult *
        calibration.damageMult *
        frameWeaponDamageMultiplier(frame, this.runInventory.salvage);
      const returnAfterS =
        def.returnAfterS ?? (slotFx.returnAfterS > 0 ? slotFx.returnAfterS : undefined);
      const returnDamageRetention = Math.max(
        def.returnDamageRetention ?? 0,
        slotFx.returnDamageRetention,
      );
      for (let i = 0; i < count; i += 1) {
        const spread = spreadBase * (i - (count - 1) / 2);
        const dir = fromAngle(this.player.facing + spread);
        this.projectiles.push({
          id: this.nextId++,
          ownerId: this.player.id,
          weaponId,
          x: this.player.x + facing.x * 1.6,
          y: this.player.y + facing.y * 1.6,
          // Compatibility/rendering coordinate only; hit eligibility is planar.
          altitude: this.player.altitude,
          vx: dir.x * projectileSpeed + this.player.vx * 0.2,
          vy: dir.y * projectileSpeed + this.player.vy * 0.2,
          life: def.life,
          damage,
          radius: def.radius,
          friendly: true,
          element: slotFx.primaryElement,
          homingTurnRate: slotFx.homing ? slotFx.homingTurnRate : 0,
          homingRangeM: slotFx.homing ? slotFx.homingRangeM : 0,
          isShrapnel: false,
          sourceSlot: def.slot,
          remainingPierces: (def.pierceCount ?? 0) + slotFx.pierceCount,
          pierceDamageRetention: Math.min(
            def.pierceDamageRetention ?? 1,
            slotFx.pierceDamageRetention,
          ),
          remainingRicochets: (def.ricochetCount ?? 0) + slotFx.ricochetCount,
          ricochetRangeM: Math.max(def.ricochetRangeM ?? 0, slotFx.ricochetRangeM),
          ricochetDamageRetention: Math.min(
            def.ricochetDamageRetention ?? 1,
            slotFx.ricochetDamageRetention,
          ),
          blastRadiusM: (def.blastRadiusM ?? 0) + slotFx.blastRadiusBonusM,
          blastDamageRetention: Math.min(
            def.blastDamageRetention ?? 1,
            slotFx.blastDamageRetention,
          ),
          implosionPullM: def.implosionPullM ?? 0,
          fragmentCount: (def.fragmentCount ?? 0) + slotFx.impactFragmentCount,
          fragmentDamageRetention: Math.min(
            def.fragmentDamageRetention ?? 1,
            slotFx.impactFragmentDamageRetention,
          ),
          fragmentSpeed: Math.max(def.fragmentSpeed ?? 0, slotFx.impactFragmentSpeed),
          returnAfterLife: returnAfterS == null ? undefined : Math.max(0, def.life - returnAfterS),
          returnDamageRetention: returnDamageRetention > 0 ? returnDamageRetention : undefined,
          returning: false,
          clearsHostileProjectilesOnBlast: slotFx.counterpulseProjectileClear,
          hitEnemyIds: [],
        });
      }
      noteDebriefWeaponShot(this.debriefStats, weaponId);
      this.pushAudio({
        type: "weapon_fire",
        weaponId,
        x: this.player.x,
        y: this.player.y,
      });
    };

    const primary = loadoutWeaponForSlot(this.runInventory.weapons, "primary");
    const secondary = loadoutWeaponForSlot(this.runInventory.weapons, "secondary");
    const ordnance = loadoutWeaponForSlot(this.runInventory.weapons, "ordnance");
    if (primary) tryFire(primary, cmd.buttons.firePrimary);
    if (secondary) tryFire(secondary, cmd.buttons.fireSecondary);
    if (ordnance) tryFire(ordnance, cmd.buttons.fireOrdnance);
  }

  /** Soft-lock for HUD/cues + fire assist; uses best equipped weapon range. */
  private refreshSoftLock(): void {
    let range = 40;
    for (const id of this.runInventory.weapons) {
      const def = WEAPONS[id];
      if (!def) continue;
      const calibration = resolveWeaponCalibration(this.banked.weaponCalibration, id);
      range = Math.max(
        range,
        weaponRangeProxy(def.projectileSpeed * calibration.projectileSpeedMult, def.life),
      );
    }
    this.assistTarget = findSoftLockTarget(
      {
        x: this.player.x,
        y: this.player.y,
        facing: this.player.facing,
        rangeProxy: range,
      },
      this.enemies,
    );
    const nextId = this.assistTarget?.id ?? null;
    if (nextId != null && nextId !== this.prevAssistTargetId && this.assistLockCd <= 0) {
      this.pushAudio({ type: "assist_lock" });
      this.softLockPulseTimer = 0.35;
      this.assistLockCd = 0.55;
    }
    this.prevAssistTargetId = nextId;
  }

  private tickAssistFeedback(dt: number): void {
    if (this.assistLockCd > 0) this.assistLockCd = Math.max(0, this.assistLockCd - dt);
    if (this.softLockPulseTimer > 0) {
      this.softLockPulseTimer = Math.max(0, this.softLockPulseTimer - dt);
    }
  }

  private weaponHudSlots(): WeaponHudSlot[] {
    const mods = this.mods();
    const slots: { slot: WeaponHudSlot["slot"]; glyph: string }[] = [
      { slot: "primary", glyph: "LMB" },
      { slot: "secondary", glyph: "Q" },
      { slot: "ordnance", glyph: "F" },
    ];
    return slots.map(({ slot, glyph }) => {
      const weaponId = loadoutWeaponForSlot(this.runInventory.weapons, slot) ?? null;
      const def = weaponId ? WEAPONS[weaponId] : undefined;
      const slotFx = this.slotModEffects(slot);
      const calibration = weaponId
        ? resolveWeaponCalibration(this.banked.weaponCalibration, weaponId)
        : null;
      const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
      const maxCd = def
        ? def.cooldown *
          mods.cooldownMult *
          slotFx.cooldownMult *
          (calibration?.cooldownMult ?? 1) *
          (frame.weaponCooldownMult ?? 1)
        : 0;
      const remaining = weaponId ? (this.weaponCd[weaponId] ?? 0) : 0;
      const fill = maxCd > 0 ? Math.min(1, remaining / maxCd) : 0;
      return {
        slot,
        weaponId,
        shortName: shortWeaponName(
          weaponId ?? undefined,
          slot === "primary" ? "PRIM" : slot === "secondary" ? "SEC" : "ORD",
        ),
        keyGlyph: glyph,
        cooldownRemaining: remaining,
        cooldownMax: maxCd,
        cooldownFill: fill,
        ready: weaponId != null && remaining <= 0,
        mods: slotFx.mods.map((m) => ({
          modId: m.modId,
          chip: m.chip,
          stacks: m.stacks,
          element: m.element,
        })),
        homing: slotFx.homing,
      };
    });
  }

  private earlyGameFactor(): number {
    if (this.simTime >= DIRECTOR_EARLY_WINDOW_S) return 0;
    return 1 - this.simTime / DIRECTOR_EARLY_WINDOW_S;
  }

  private isEarlyGame(): boolean {
    return this.simTime < DIRECTOR_EARLY_WINDOW_S;
  }

  private turretTelegraphDuration(): number {
    const early = this.earlyGameFactor();
    return TURRET_TELEGRAPH_S + (TURRET_TELEGRAPH_EARLY_S - TURRET_TELEGRAPH_S) * early;
  }

  /** Fire-rate slowdown early (1 = normal, up to ~1.7x cooldown). */
  private earlyFireCooldownMult(): number {
    return 1 + 0.7 * this.earlyGameFactor();
  }

  /** Aim scatter radians — higher early so volleys are dodgeable. */
  private earlyAimScatterRad(): number {
    return 0.28 * this.earlyGameFactor();
  }

  private directorMaxActive(): number {
    const t = this.simTime;
    let timeCap = 3;
    if (t >= 45) timeCap = 4;
    if (t >= 90) timeCap = 6;
    if (t >= 150) timeCap = 8;
    if (t >= 240) timeCap = DIRECTOR_MAX_ACTIVE;
    const area = this.streamer.chunkAt(this.player.x, this.player.y);
    const biomeBonus = area.dangerTier >= 3 ? 2 : area.dangerTier >= 2 ? 1 : 0;
    return Math.min(
      DIRECTOR_MAX_ACTIVE_CAP,
      timeCap + Math.floor(this.runProgress.level / 5) + biomeBonus,
    );
  }

  private directorSpawnCooldown(biomeMult: number): number {
    const earlyMult = 1 + 0.75 * this.earlyGameFactor();
    const base = Math.max(
      DIRECTOR_SPAWN_COOLDOWN_MIN_S,
      DIRECTOR_SPAWN_COOLDOWN_S - Math.floor(this.runProgress.level / 4) * 0.08,
    );
    const contractMult = activeSortieContract(this.activeContractId)?.spawnCooldownMult ?? 1;
    return base * biomeMult * earlyMult * contractMult;
  }

  private enqueueSpawns(specs: IncursionSpawnSpec[]): void {
    this.spawnQueue.push(...specs);
  }

  private drainSpawnQueue(dt: number): void {
    if (this.spawnQueue.length === 0) return;
    this.spawnQueueCd -= dt;
    if (this.spawnQueueCd > 0) return;
    if (this.enemies.length >= this.directorMaxActive()) {
      this.spawnQueueCd = DIRECTOR_SPAWN_STAGGER_S;
      return;
    }
    const next = this.spawnQueue.shift();
    if (!next) return;
    this.spawnEnemy(next.role, next.elite);
    this.spawnQueueCd = DIRECTOR_SPAWN_STAGGER_S;
  }

  private updateIncursion(dt: number): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding") return;

    const signal = tickIncursion(this.incursion, this.simTime, dt, this.seed, this.tick);
    if (signal === "end") {
      const intervalMult = activeSortieContract(this.activeContractId)?.incursionIntervalMult ?? 1;
      if (intervalMult !== 1) {
        this.incursion.nextAt =
          this.simTime + (this.incursion.nextAt - this.simTime) * intervalMult;
      }
      this.runIncursionSurvived = true;
      this.message = "Incursion subsiding — threat easing.";
      return;
    }
    if (signal !== "start") return;

    const waveId = rollIncursionWave(this.seed, this.tick);
    startIncursion(this.incursion, this.simTime, waveId);
    this.message = `INCURSION — ${INCURSION_LABELS[waveId]}`;
    this.pushAudio({
      type: "telegraph_warning",
      kind: "turret",
      x: this.player.x,
      y: this.player.y,
    });
    this.telemetry("incursion_start", { waveId });
    // Stagger the wave — never dump a firing squad in one frame.
    this.enqueueSpawns(incursionSpawnBatch(waveId, this.runProgress.level));
    this.spawnQueueCd = 0;
  }

  private updateAdaptiveDirector(dt: number): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding") return;
    this.adaptiveTimer += dt;
    if (this.adaptiveTimer < 5) return;
    this.adaptiveTimer = 0;
    const killRate = this.recentKills / 5;
    const damageRate = this.recentHullDamage / 5;
    if (killRate > 3 && damageRate < 5)
      this.directorPressure = Math.min(1.35, this.directorPressure + 0.05);
    else if (damageRate > 15) this.directorPressure = Math.max(0.75, this.directorPressure - 0.05);
    else this.directorPressure = this.directorPressure * 0.95 + 1 * 0.05;
    this.recentKills = 0;
    this.recentHullDamage = 0;
  }

  private updateBounty(dt: number): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding") return;
    const signal = tickBounty(
      this.bounty,
      this.simTime,
      dt,
      this.enemies,
      this.player,
      this.seed,
      this.tick,
    );
    if (signal === "mark") {
      this.message = "PRIORITY TARGET marked — drop it for bonus score.";
      this.pushAudio({ type: "elite_spawn", x: this.player.x, y: this.player.y });
    }
  }

  private noteDaily(event: DailyOpEvent): void {
    this.refreshDailyOps();
    const newly = applyDailyOpEvent(this.banked.dailyOps, event);
    let salvage = 0;
    let rp = 0;
    for (const op of newly) {
      if (!claimDailyOp(op)) continue;
      salvage += op.salvageReward;
      rp += op.rpReward;
    }
    if (salvage > 0) this.banked.salvage += salvage;
    if (rp > 0) applyRankPoints(this.banked.pilotMeta, rp);
    if (newly.length === 1) {
      const op = newly[0]!;
      this.message = `Daily ops: ${op.title} complete. +${op.salvageReward} salvage.`;
    } else if (newly.length > 1) {
      this.message = `Daily ops: ${newly.length} contracts complete.`;
    }
  }
  private updateDirector(dt: number): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding") return;
    this.drainSpawnQueue(dt);
    this.directorCd -= dt;
    if (this.relayLullTimer > 0) return;
    if (this.enemies.length >= this.directorMaxActive()) return;
    if (this.directorCd > 0) return;
    if (this.simTime < DIRECTOR_GRACE_S && this.phase === "expedition") return;

    const area = this.areaPressure();
    const budget = area.budgetMax;
    const spent = this.enemies.reduce((sum, e) => {
      const role = ENEMY_ROLES[e.role];
      return sum + (role?.threatCost ?? 1) * (e.elite ? 3 : 1);
    }, 0);
    this.threatSpent = spent;
    if (spent >= budget) return;

    if (this.phase === "onboarding") {
      this.spawnOnboardingEnemies();
      this.directorCd = this.directorSpawnCooldown(area.biome.spawnCooldownMult);
      return;
    }

    const rng = createRng(hashSeed(this.seed, this.tick));
    const roles = this.weightedRolesForArea(area.biome.heavyBias, area.dangerTier);
    const role = pickWeighted(rng, roles);
    const earlyEliteScale = this.isEarlyGame() ? 0.35 : 1;
    const eliteChance =
      (area.biome.eliteChance +
        area.chunk.hazard * 0.08 +
        this.runProgress.level * DIRECTOR_ELITE_CHANCE_PER_LEVEL +
        (activeSortieContract(this.activeContractId)?.eliteChanceAdd ?? 0)) *
      earlyEliteScale;
    const eliteRoll = rng() < eliteChance;
    this.spawnEnemy(role.id, eliteRoll);
    this.directorCd = this.directorSpawnCooldown(area.biome.spawnCooldownMult);

    if (!this.eliteSpawned && this.simTime > 95) {
      this.enqueueSpawns([{ role: "skirmisher", elite: true }]);
      this.eliteSpawned = true;
      this.message = "Elite signature marked — retrieve its core.";
    }

    const grade = threatGradeForLevel(this.runProgress.level);
    if (
      grade >= 6 &&
      !this.aceSpawned &&
      !this.enemies.some((e) => e.isAce) &&
      this.simTime > 110 &&
      this.enemies.length < this.directorMaxActive()
    ) {
      this.spawnEnemy("ace", true, { isAce: true, affix: "swift" });
      this.aceSpawned = true;
      this.message = "ACE inbound — threat grade critical.";
      this.telemetry("ace_spawn", { grade });
    }
  }

  /** Area-scaled threat ceiling from biome + chunk hazard + time + pilot level. */
  private areaPressure(): {
    chunk: ChunkData;
    biome: (typeof BIOMES)[keyof typeof BIOMES];
    dangerTier: ChunkData["dangerTier"];
    budgetMax: number;
  } {
    const chunk = this.streamer.chunkAt(this.player.x, this.player.y);
    const biome = BIOMES[chunk.biome];
    // Slow ramp: +1 budget every ~70s so the first minutes stay sparse.
    const timeBudget = DIRECTOR_BASE_BUDGET + Math.floor(this.simTime / 70);
    const hazardScale = 0.7 + chunk.hazard * 0.45;
    const pilotMult = 1 + (this.runProgress.level - 1) * DIRECTOR_LEVEL_THREAT_PER_LEVEL;
    const earlyFloor = this.isEarlyGame() ? 2 : 3;
    const contractMult = activeSortieContract(this.activeContractId)?.threatBudgetMult ?? 1;
    const budgetMax = Math.max(
      earlyFloor,
      Math.round(
        timeBudget *
          biome.threatMult *
          hazardScale *
          (0.85 + chunk.dangerTier * 0.07) *
          pilotMult *
          this.incursion.budgetMult *
          this.directorPressure *
          contractMult,
      ),
    );
    return { chunk, biome, dangerTier: chunk.dangerTier, budgetMax };
  }

  private weightedRolesForArea(
    heavyBias: number,
    dangerTier: number,
  ): Array<EnemyRoleDef & { weight: number }> {
    const level = this.runProgress.level;
    return Object.values(ENEMY_ROLES)
      .filter(
        (role) =>
          roleAllowedAtLevel(role.id, level) &&
          roleAllowedAtPilotRank(role.id, this.banked.pilotMeta.rank),
      )
      .map((role) => {
        const heavy =
          role.id === "turret" ||
          role.id === "bomber" ||
          role.id === "skirmisher" ||
          role.id === "carrier" ||
          role.id === "sniper" ||
          role.id === "tender" ||
          role.id === "leech";
        let weight = heavy
          ? role.weight * heavyBias * (0.85 + dangerTier * 0.2)
          : role.weight * (dangerTier >= 3 ? 0.7 : 1);
        if (role.id === "breacher" && dangerTier >= 2) weight *= 1.2;
        if (role.id === "marauder" && dangerTier >= 2) weight *= 1.25;
        return { ...role, weight: Math.max(0.05, weight) };
      });
  }

  private spawnOnboardingEnemies(): void {
    // At most one spawn per director tick so onboarding never dumps a squad.
    if (
      this.onboardingStep >= ONBOARDING_STEP.AIM_FIRE &&
      this.enemies.filter((e) => e.role === "scout").length < 1
    ) {
      this.spawnEnemy("scout", false);
      return;
    }
    if (
      this.onboardingStep >= ONBOARDING_STEP.QF_LITERACY &&
      this.enemies.filter((e) => e.role === "skirmisher").length < 1
    ) {
      this.spawnEnemy("skirmisher", false);
      return;
    }
    if (
      this.onboardingStep >= ONBOARDING_STEP.QF_LITERACY &&
      this.enemies.filter((e) => e.role === "turret").length < 1
    ) {
      this.spawnEnemy("turret", false);
      return;
    }
    if (this.onboardingStep >= ONBOARDING_STEP.CARRIER_HOME && !this.eliteSpawned) {
      this.spawnEnemy("bomber", true);
      this.eliteSpawned = true;
      this.message = "Elite core carrier inbound — recover the core and extract.";
    }
  }

  private spawnEnemy(
    roleId: EnemyRoleId,
    elite: boolean,
    opts: {
      isAce?: boolean;
      affix?: EliteAffixId | null;
      x?: number;
      y?: number;
      isArenaGuardian?: boolean;
      /** Skip default elite_spawn sting (boss enter plays its own). */
      silentSpawnAudio?: boolean;
    } = {},
  ): void {
    const role = ENEMY_ROLES[roleId];
    if (!role) return;
    const rng = createRng(hashSeed(this.seed, this.tick * 13 + this.enemies.length * 7));
    const facingAway = this.player.facing + Math.PI;
    const blind = (DIRECTOR_BLIND_SPOT_DEG * Math.PI) / 180;
    const angle = facingAway + rngRange(rng, -blind, blind);
    const dist = rngRange(rng, 55, 95);
    const x = opts.x ?? this.player.x + Math.cos(angle) * dist;
    const y = opts.y ?? this.player.y + Math.sin(angle) * dist;
    const isAce = opts.isAce ?? roleId === "ace";
    const isArenaGuardian = opts.isArenaGuardian ?? false;
    const eliteMult = elite || isAce || isArenaGuardian ? 2.2 : 1;
    const tier = tierMultForLevel(this.runProgress.level);
    const affix =
      opts.affix !== undefined
        ? opts.affix
        : elite || isAce || isArenaGuardian
          ? rollEliteAffix(this.seed, this.nextId, this.tick)
          : null;
    const affixDef = affix ? ELITE_AFFIXES[affix] : null;
    const hull = role.hull * eliteMult * tier.hull * (affixDef?.hullMult ?? 1) * (isAce ? 1.3 : 1);
    const shield = role.shield * eliteMult * (affixDef?.shieldMult ?? 1);
    const facePlayer = angleOf({ x: this.player.x - x, y: this.player.y - y });
    this.enemies.push({
      id: this.nextId++,
      role: roleId,
      x,
      y,
      // All combat ships share one absolute world-Y plane. Terrain elevation
      // remains visual/physical ground data, never a second flight axis.
      altitude: this.player.altitude,
      vx: 0,
      vy: 0,
      facing: opts.x !== undefined ? facePlayer : angle + Math.PI,
      hull,
      maxHull: hull,
      shield,
      maxShield: shield,
      // Stagger first shot so a fresh spawn never opens fire immediately.
      fireCooldown:
        rngRange(rng, role.fireCooldown * 0.9, role.fireCooldown * 1.8) *
        this.earlyFireCooldownMult(),
      elite: elite || isAce || isArenaGuardian,
      aiTimer: 0,
      aiState: defaultAiState(roleId),
      spawnTier: spawnTierForLevel(this.runProgress.level),
      telegraphTimer: isArenaGuardian ? 1.1 : 0,
      burstCd: 0,
      eliteAffix: affix,
      isAce,
      isArenaGuardian,
      phaseTimer: 0,
      burnTimer: 0,
      burnDps: 0,
      slowTimer: 0,
      slowFactor: 0,
      shredTimer: 0,
      hullShred: 0,
      ionTimer: 0,
      shieldWeakened: 0,
      shockTimer: 0,
      stolenSalvage: 0,
    });
    if ((elite || isAce || isArenaGuardian) && !opts.silentSpawnAudio) {
      this.pushAudio({ type: "elite_spawn", x, y });
    }
  }

  private updateEnemies(dt: number): void {
    this.telegraphs = [];
    for (const enemy of this.enemies) {
      const role = ENEMY_ROLES[enemy.role]!;
      const telegraphBefore = enemy.telegraphTimer;
      enemy.aiTimer += dt;
      enemy.fireCooldown -= dt;
      enemy.burstCd -= dt;
      enemy.telegraphTimer -= dt;

      const burnDmg = tickEnemyStatuses(enemy, dt);
      if (burnDmg > 0 && enemy.hull > 0) {
        this.damageEnemy(enemy, burnDmg, undefined, { skipPayload: true });
      }
      if (enemy.hull <= 0) continue;

      const tier = tierMultForLevel(this.runProgress.level);
      const dmgMult = tier.damage * (enemy.elite ? 1.4 : 1);
      const cryoMult = enemy.slowTimer > 0 ? 1 - enemy.slowFactor : 1;
      const speedMult = tier.speed * cryoMult;
      const scaledRole = { ...role, speed: role.speed * speedMult };

      const salvageTarget = role.id === "scavenger" ? this.nearestLooseSalvage(enemy) : null;
      const bossOwner =
        enemy.bossMinionOwnerId == null
          ? null
          : (this.enemies.find(
              (candidate) => candidate.id === enemy.bossMinionOwnerId && candidate.hull > 0,
            ) ?? null);
      const returningToLeviathan =
        role.id === "marauder" && (enemy.stolenSalvage ?? 0) > 0 && bossOwner?.role === "leviathan";
      const motionTarget = returningToLeviathan
        ? { ...this.player, x: bossOwner.x, y: bossOwner.y, vx: 0, vy: 0 }
        : salvageTarget
          ? { ...this.player, x: salvageTarget.x, y: salvageTarget.y, vx: 0, vy: 0 }
          : this.player;
      const motion = updateEnemyMotion(enemy, scaledRole, motionTarget, dt);
      // World-Y is presentation/save compatibility only. Pin every target to
      // the player's current absolute flight plane so a slope cannot make an
      // enemy appear vertically unreachable.
      enemy.altitude = this.player.altitude;

      if (motion.boostPulse) {
        this.pushAudio({ type: "enemy_boost", x: enemy.x, y: enemy.y });
      }

      if (returningToLeviathan && bossOwner) {
        if (dist2(enemy, bossOwner) <= LEVIATHAN_DOCK_RADIUS_M) {
          const deposited = enemy.stolenSalvage ?? 0;
          enemy.stolenSalvage = 0;
          bossOwner.stolenSalvage = (bossOwner.stolenSalvage ?? 0) + deposited;
          const restored = Math.min(
            bossOwner.maxShield - bossOwner.shield,
            deposited * LEVIATHAN_SCRAP_SHIELD_RESTORE,
          );
          bossOwner.shield += restored;
          enemy.fireCooldown = role.fireCooldown;
          this.message = `LEVIATHAN BANKED ${deposited} SCRAP — shield restored by ${Math.round(restored)}.`;
          this.telemetry("leviathan_salvage_banked", {
            enemyId: bossOwner.id,
            marauderId: enemy.id,
            amount: deposited,
            shieldRestored: restored,
          });
        }
        continue;
      }

      if (role.id === "scavenger" && salvageTarget && dist2(enemy, salvageTarget) < 3.4) {
        const dropIndex = this.drops.findIndex((drop) => drop.id === salvageTarget.id);
        if (dropIndex >= 0) {
          const [stolen] = this.drops.splice(dropIndex, 1);
          if (stolen) {
            enemy.stolenSalvage = (enemy.stolenSalvage ?? 0) + stolen.amount;
            this.message = `SCAVENGER STOLE ${stolen.amount} SCRAP — destroy it to reclaim the cargo.`;
            this.telemetry("salvage_stolen", {
              enemyId: enemy.id,
              amount: stolen.amount,
              carried: enemy.stolenSalvage,
            });
          }
        }
      }

      if (role.id === "harvester") {
        this.updateHarvesterVacuum(enemy, dt);
      }

      if (role.id === "sovereign") {
        const hullRatio = enemy.hull / Math.max(1, enemy.maxHull);
        if (hullRatio < 0.66 && enemy.phaseTimer < 1) {
          this.spawnEnemy("tender", true, {
            x: enemy.x - 6,
            y: enemy.y,
            affix: null,
            silentSpawnAudio: true,
          });
          enemy.phaseTimer = 1;
          this.message = "Velocity Sovereign deployed an Aegis Tender.";
        } else if (hullRatio < 0.33 && enemy.phaseTimer < 2) {
          for (const offset of [-7, 7]) {
            this.spawnEnemy("tender", true, {
              x: enemy.x + offset,
              y: enemy.y - 3,
              affix: null,
              silentSpawnAudio: true,
            });
          }
          enemy.phaseTimer = 2;
          this.message = "Velocity Sovereign reinforced its Aegis screen.";
        }
      }
      if (role.id === "leviathan") {
        const hullRatio = enemy.hull / Math.max(1, enemy.maxHull);
        if (hullRatio < 0.66 && enemy.phaseTimer < 1) {
          this.spawnChildMarauder(enemy, -6, -2);
          this.spawnChildMarauder(enemy, 6, 2);
          enemy.phaseTimer = 1;
          this.message = "Foundry Leviathan launched a boarding wing.";
        } else if (hullRatio < 0.33 && enemy.phaseTimer < 2) {
          for (const offset of [-7, 0, 7]) this.spawnChildMarauder(enemy, offset, -4);
          enemy.phaseTimer = 2;
          this.message = "Foundry Leviathan opened every boarding dock.";
        }
      }
      if (role.id === "null_crown") {
        const activeNodes = this.enemies.filter(
          (candidate) =>
            candidate.role === "nullifier" &&
            candidate.bossMinionOwnerId === enemy.id &&
            candidate.hull > 0,
        );
        if (activeNodes.length > 0) {
          enemy.shield = Math.min(
            enemy.maxShield,
            enemy.shield + activeNodes.length * NULL_CROWN_NODE_SHIELD_REGEN_PER_S * dt,
          );
        }
        const hullRatio = enemy.hull / Math.max(1, enemy.maxHull);
        if (hullRatio < 0.66 && enemy.phaseTimer < 1) {
          this.spawnChildNullifier(enemy, -8, -3);
          this.spawnChildNullifier(enemy, 8, 3);
          enemy.phaseTimer = 1;
          this.message = "Null Crown deployed a regenerating suppression lattice.";
        } else if (hullRatio < 0.33 && enemy.phaseTimer < 2) {
          for (const offset of [-10, 0, 10]) this.spawnChildNullifier(enemy, offset, -5);
          enemy.phaseTimer = 2;
          this.message = "Null Crown lattice at full strength — break the nodes.";
        }
      }
      if (role.id === "centurion") {
        const hullRatio = enemy.hull / Math.max(1, enemy.maxHull);
        if (hullRatio < 0.75 && enemy.phaseTimer < 1) {
          this.triggerCenturionCounterpulse(enemy);
          enemy.phaseTimer = 1;
        } else if (hullRatio < 0.5 && enemy.phaseTimer < 2) {
          this.triggerCenturionCounterpulse(enemy);
          enemy.phaseTimer = 2;
        } else if (hullRatio < 0.25 && enemy.phaseTimer < 3) {
          this.triggerCenturionCounterpulse(enemy);
          enemy.phaseTimer = 3;
        }
      }
      if (role.id === "tender") {
        this.updateTenderSupport(enemy, dt);
      }

      if (motion.meleeDamage > 0 && this.player.invulnTimer <= 0) {
        if (role.id === "marauder") this.raidCarriedSalvage(enemy);
        this.damagePlayer(motion.meleeDamage, {
          x: enemy.x,
          y: enemy.y,
          role: enemy.role,
          elite: enemy.elite,
          isAce: enemy.isAce,
          isArenaGuardian: enemy.isArenaGuardian,
          attackKind: "melee",
        });
      }

      if (
        role.id === "bomber" &&
        enemy.hull < enemy.maxHull * 0.5 &&
        enemy.aiTimer > 2.5 &&
        this.tick % 120 === enemy.id % 120
      ) {
        this.dropMine(enemy.x, enemy.y, enemy.altitude, dmgMult);
        enemy.aiTimer = 0;
      }

      if (role.id === "warden") {
        const hullRatio = enemy.hull / Math.max(1, enemy.maxHull);
        if (hullRatio < 0.66 && enemy.phaseTimer < 1) {
          this.spawnChildScout(enemy.x - 3, enemy.y - 2, true);
          this.spawnChildScout(enemy.x + 3, enemy.y + 2, true);
          enemy.phaseTimer = 1;
          this.message = "Siege Warden deployed an interceptor screen.";
        } else if (hullRatio < 0.33 && enemy.phaseTimer < 2) {
          for (const offset of [-4, 0, 4]) {
            this.spawnChildScout(enemy.x + offset, enemy.y - 3, true);
          }
          enemy.phaseTimer = 2;
          this.message = "Siege Warden last stand — interceptor screen reinforced.";
        }
      }

      if (role.id === "harvester") {
        const hullRatio = enemy.hull / Math.max(1, enemy.maxHull);
        if (hullRatio < 0.66 && enemy.phaseTimer < 1) {
          this.spawnChildScavenger(enemy, -4, -2);
          this.spawnChildScavenger(enemy, 4, 2);
          enemy.phaseTimer = 1;
          this.message = "Dread Harvester deployed salvage collectors.";
        } else if (hullRatio < 0.33 && enemy.phaseTimer < 2) {
          for (const offset of [-5, 0, 5]) this.spawnChildScavenger(enemy, offset, -4);
          enemy.phaseTimer = 2;
          this.message = "Dread Harvester opened every intake — reclaim the field.";
        }
      }

      if (role.id === "turret") {
        const telegraphS = this.turretTelegraphDuration();
        if (enemy.telegraphTimer > 0) {
          this.telegraphs.push({
            enemyId: enemy.id,
            kind: "turret_aim",
            x: this.player.x,
            y: this.player.y,
            life: enemy.telegraphTimer,
          });
        } else if (motion.fireReady && enemy.fireCooldown <= 0 && telegraphBefore <= 0) {
          enemy.telegraphTimer = telegraphS;
          this.pushAudio({
            type: "telegraph_warning",
            kind: "turret",
            x: enemy.x,
            y: enemy.y,
          });
          this.telegraphs.push({
            enemyId: enemy.id,
            kind: "turret_aim",
            x: this.player.x,
            y: this.player.y,
            life: telegraphS,
          });
        } else if (telegraphBefore > 0 && enemy.telegraphTimer <= 0) {
          const aim = this.applyAimScatter(
            leadAimDirection(enemy, this.player, role.projectileSpeed),
            enemy.id,
          );
          enemy.fireCooldown = role.fireCooldown * this.earlyFireCooldownMult();
          this.fireEnemyShot(enemy, role, aim, dmgMult);
        }
        continue;
      }
      if (!motion.fireReady || enemy.fireCooldown > 0) continue;

      const fireMult = this.earlyFireCooldownMult();
      if (role.id === "scout") {
        if (enemy.burstCd > 0) continue;
        enemy.burstCd = 0.45;
        enemy.fireCooldown = role.fireCooldown * 0.55 * fireMult;
      } else {
        enemy.fireCooldown = role.fireCooldown * fireMult;
      }

      const rawAim = normalize2(vec2(this.player.x - enemy.x, this.player.y - enemy.y));
      if (role.id === "warden") {
        enemy.fireCooldown = role.fireCooldown * fireMult;
        const center = Math.atan2(rawAim.y, rawAim.x);
        const volleyRole = { ...scaledRole, damage: scaledRole.damage * 0.72 };
        for (const offset of [-0.22, 0, 0.22]) {
          this.fireEnemyShot(enemy, volleyRole, fromAngle(center + offset), dmgMult);
        }
        continue;
      }
      if (role.id === "harvester") {
        enemy.fireCooldown = role.fireCooldown * fireMult;
        const center = Math.atan2(rawAim.y, rawAim.x);
        const volleyRole = { ...scaledRole, damage: scaledRole.damage * 0.52 };
        for (const offset of [-0.32, -0.16, 0, 0.16, 0.32]) {
          this.fireEnemyShot(enemy, volleyRole, fromAngle(center + offset), dmgMult);
        }
        continue;
      }
      if (role.id === "sovereign") {
        enemy.fireCooldown = role.fireCooldown * fireMult;
        const center = Math.atan2(rawAim.y, rawAim.x);
        const volleyRole = { ...scaledRole, damage: scaledRole.damage * 0.46 };
        for (const offset of [-0.36, -0.24, -0.12, 0, 0.12, 0.24, 0.36]) {
          this.fireEnemyShot(enemy, volleyRole, fromAngle(center + offset), dmgMult);
        }
        continue;
      }
      if (role.id === "leviathan") {
        enemy.fireCooldown = role.fireCooldown * fireMult;
        const center = Math.atan2(rawAim.y, rawAim.x);
        const volleyRole = { ...scaledRole, damage: scaledRole.damage * 0.4 };
        for (const offset of [-0.48, -0.36, -0.24, -0.12, 0, 0.12, 0.24, 0.36, 0.48]) {
          this.fireEnemyShot(enemy, volleyRole, fromAngle(center + offset), dmgMult);
        }
        continue;
      }
      if (role.id === "null_crown") {
        enemy.fireCooldown = role.fireCooldown * fireMult;
        const center = Math.atan2(rawAim.y, rawAim.x);
        const volleyRole = { ...scaledRole, damage: scaledRole.damage * 0.36 };
        for (const offset of [-0.6, -0.48, -0.36, -0.24, -0.12, 0, 0.12, 0.24, 0.36, 0.48, 0.6]) {
          this.fireEnemyShot(enemy, volleyRole, fromAngle(center + offset), dmgMult);
        }
        continue;
      }
      if (role.id === "centurion") {
        enemy.fireCooldown = role.fireCooldown * fireMult;
        const center = Math.atan2(rawAim.y, rawAim.x);
        const volleyRole = { ...scaledRole, damage: scaledRole.damage * 0.32 };
        for (const offset of [
          -0.72, -0.6, -0.48, -0.36, -0.24, -0.12, 0, 0.12, 0.24, 0.36, 0.48, 0.6, 0.72,
        ]) {
          this.fireEnemyShot(enemy, volleyRole, fromAngle(center + offset), dmgMult);
        }
        continue;
      }
      const aim = this.applyAimScatter(rawAim, enemy.id);
      this.fireEnemyShot(enemy, scaledRole, aim, dmgMult);
    }
  }

  private nearestLooseSalvage(enemy: EnemyState): DropState | null {
    let nearest: DropState | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (const drop of this.drops) {
      if (drop.kind !== "salvage") continue;
      const distance = dist2(enemy, drop);
      if (distance >= nearestDistance) continue;
      nearest = drop;
      nearestDistance = distance;
    }
    return nearest;
  }

  private triggerCenturionCounterpulse(enemy: EnemyState): void {
    let intercepted = 0;
    this.projectiles = this.projectiles.filter((shot) => {
      if (!shot.friendly || dist2(shot, enemy) > CENTURION_COUNTERPULSE_RADIUS_M) return true;
      intercepted += 1;
      return false;
    });
    const restored = Math.min(
      enemy.maxShield - enemy.shield,
      CENTURION_COUNTERPULSE_BASE_SHIELD + intercepted * CENTURION_COUNTERPULSE_SHIELD_PER_ROUND,
    );
    enemy.shield += restored;

    const role = ENEMY_ROLES.centurion!;
    const counterRole = { ...role, damage: role.damage * 0.3 };
    for (let index = 0; index < 12; index += 1) {
      this.fireEnemyShot(enemy, counterRole, fromAngle((index / 12) * Math.PI * 2), 1);
    }
    this.message = `CENTURION COUNTERPULSE — ${intercepted} rounds erased, shield +${Math.round(restored)}.`;
    this.telemetry("centurion_counterpulse", {
      enemyId: enemy.id,
      intercepted,
      shieldRestored: restored,
      phase: enemy.phaseTimer + 1,
    });
  }

  private raidCarriedSalvage(enemy: EnemyState): void {
    const carried = this.runInventory.salvage;
    if (carried <= 0) return;
    const amount = Math.min(carried, Math.min(18, Math.max(4, Math.ceil(carried * 0.12))));
    this.runInventory.salvage -= amount;
    enemy.stolenSalvage = (enemy.stolenSalvage ?? 0) + amount;
    this.message = `MARAUDER BOARDED — ${amount} carried scrap stolen. Destroy it to reclaim the cargo.`;
    this.telemetry("salvage_raided", {
      enemyId: enemy.id,
      amount,
      carried: enemy.stolenSalvage,
    });
  }

  private updateHarvesterVacuum(enemy: EnemyState, dt: number): void {
    const target = this.nearestLooseSalvage(enemy);
    if (!target) return;
    const dx = enemy.x - target.x;
    const dy = enemy.y - target.y;
    const distance = Math.hypot(dx, dy);
    if (distance > HARVESTER_VACUUM_RANGE_M) return;

    if (distance > HARVESTER_INTAKE_RADIUS_M) {
      const step = Math.min(distance - HARVESTER_INTAKE_RADIUS_M, HARVESTER_PULL_SPEED_MPS * dt);
      target.x += (dx / distance) * step;
      target.y += (dy / distance) * step;
      if (distance - step > HARVESTER_INTAKE_RADIUS_M) return;
    }

    const dropIndex = this.drops.findIndex((drop) => drop.id === target.id);
    if (dropIndex < 0) return;
    const [captured] = this.drops.splice(dropIndex, 1);
    if (!captured) return;
    enemy.stolenSalvage = (enemy.stolenSalvage ?? 0) + captured.amount;
    this.message = `HARVESTER CAPTURED ${captured.amount} SCRAP — destroy it to recover the cargo.`;
    this.telemetry("harvester_salvage_captured", {
      enemyId: enemy.id,
      amount: captured.amount,
      carried: enemy.stolenSalvage,
    });
  }

  private updateTenderSupport(tender: EnemyState, dt: number): void {
    for (const ally of this.enemies) {
      if (ally.id === tender.id || ally.hull <= 0 || ally.shield >= ally.maxShield) continue;
      if (dist2(tender, ally) > TENDER_SUPPORT_RANGE_M) continue;
      ally.shield = Math.min(ally.maxShield, ally.shield + TENDER_SHIELD_REGEN_PER_S * dt);
    }
  }

  private applyAimScatter(dir: { x: number; y: number }, salt: number): { x: number; y: number } {
    const scatter = this.earlyAimScatterRad();
    if (scatter <= 0.001) return dir;
    const rng = createRng(hashSeed(this.seed, this.tick * 31 + salt * 17));
    const angle = Math.atan2(dir.y, dir.x) + (rng() * 2 - 1) * scatter;
    return fromAngle(angle);
  }

  private fireEnemyShot(
    enemy: EnemyState,
    role: EnemyRoleDef,
    dir: { x: number; y: number },
    dmgMult: number,
  ): void {
    if (role.projectileSpeed <= 0) return;
    this.projectiles.push({
      id: this.nextId++,
      ownerId: enemy.id,
      weaponId: "arc_lance",
      x: enemy.x,
      y: enemy.y,
      altitude: enemy.altitude,
      vx: dir.x * role.projectileSpeed,
      vy: dir.y * role.projectileSpeed,
      life: 1.4,
      damage: role.damage * dmgMult,
      radius: enemy.role === "bomber" ? 2.2 : 0.5,
      friendly: false,
      element: null,
      homingTurnRate: 0,
      homingRangeM: 0,
      isShrapnel: false,
      sourceSlot: null,
    });
    this.pushAudio({ type: "enemy_fire", x: enemy.x, y: enemy.y });
  }

  /** Continuous proximity bed — intensity from nearest/clustered threats. */
  private pushApproachPresence(): void {
    if (this.enemies.length === 0) return;
    const hear = 95;
    let intensity = 0;
    for (const enemy of this.enemies) {
      const d = dist2(enemy, this.player);
      if (d >= hear) continue;
      const t = 1 - d / hear;
      intensity += t * t * (enemy.elite || enemy.isAce ? 1.35 : 1);
    }
    intensity = clamp(intensity * 0.55, 0, 1);
    if (intensity > 0.03) this.pushAudio({ type: "approach", intensity });
  }

  private dropMine(x: number, y: number, altitude: number, dmgMult: number): void {
    this.hazards.push({
      id: this.nextId++,
      kind: "mine",
      x,
      y,
      altitude,
      life: 4,
      radius: 3.2,
      damage: 36 * dmgMult,
    });
    this.telegraphs.push({ enemyId: 0, kind: "mine", x, y, life: 1 });
  }

  private updateHazards(dt: number): void {
    const kept: HazardState[] = [];
    for (const hazard of this.hazards) {
      const prevLife = hazard.life;
      hazard.life -= dt;
      const mineEarly = this.mods().mineTelegraphEarlyS;
      const warnAt = 1.5 + mineEarly;
      const showAt = 3 + mineEarly;
      if (prevLife >= warnAt && hazard.life < warnAt && hazard.kind === "mine") {
        this.pushAudio({
          type: "telegraph_warning",
          kind: "mine",
          x: hazard.x,
          y: hazard.y,
        });
      }
      if (hazard.life <= 0) continue;
      if (hazard.life < showAt && hazard.kind === "mine") {
        this.telegraphs.push({
          enemyId: hazard.id,
          kind: "mine",
          x: hazard.x,
          y: hazard.y,
          life: hazard.life,
        });
      }
      if (
        this.player.invulnTimer <= 0 &&
        dist2(hazard, this.player) <= hazard.radius + PLAYER_RADIUS_M
      ) {
        this.damagePlayer(hazard.damage, {
          x: hazard.x,
          y: hazard.y,
          role: "bomber",
          attackKind: "mine",
          attackLabel: "mine",
        });
        continue;
      }
      kept.push(hazard);
    }
    this.hazards = kept;
  }

  private updateProjectiles(dt: number): void {
    const next: ProjectileState[] = [];
    const homingTargets = this.enemies
      .filter((e) => e.hull > 0)
      .map((e) => ({ id: e.id, x: e.x, y: e.y }));
    const nullifiers = this.enemies.filter(
      (enemy) => (enemy.role === "nullifier" || enemy.role === "null_crown") && enemy.hull > 0,
    );
    for (const shot of this.projectiles) {
      const start = { x: shot.x, y: shot.y };
      shot.life -= dt;
      if (
        shot.friendly &&
        !shot.returning &&
        shot.returnAfterLife != null &&
        shot.life <= shot.returnAfterLife
      ) {
        shot.returning = true;
        shot.damage *= shot.returnDamageRetention ?? 1;
        // Outbound targets may be struck once more on the distinct return leg.
        shot.hitEnemyIds = [];
        shot.remainingPierces = Math.max(1, shot.remainingPierces ?? 0);
        this.telemetry("projectile_recall", { weaponId: shot.weaponId });
      }
      const guidanceSuppressed = nullifiers.some(
        (nullifier) => dist2(shot, nullifier) < NULLIFIER_FIELD_RADIUS_M,
      );
      if (shot.returning) {
        const speed = Math.max(1, Math.hypot(shot.vx, shot.vy));
        const home = vec2(this.player.x - shot.x, this.player.y - shot.y);
        const direction = len2(home) > 0.01 ? normalize2(home) : vec2(0, 0);
        shot.vx = direction.x * speed;
        shot.vy = direction.y * speed;
      } else if (shot.friendly && shot.homingTurnRate > 0 && !guidanceSuppressed) {
        steerProjectileHoming(shot, homingTargets, shot.homingTurnRate, shot.homingRangeM, dt);
      }
      shot.x += shot.vx * dt;
      shot.y += shot.vy * dt;
      const end = { x: shot.x, y: shot.y };
      // Projectiles share the same absolute combat plane as every ship.
      shot.altitude = this.player.altitude;
      if (shot.life <= 0) {
        if (shot.friendly) this.detonateProjectile(shot, null);
        continue;
      }

      if (
        shot.returning &&
        segmentCircleHitFraction(start, end, this.player, shot.radius + PLAYER_RADIUS_M) != null
      ) {
        const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
        const cycleReduction = frame.projectileRecallCycleReduction ?? 0;
        const boostRestore = frame.projectileRecallBoostRestore ?? 0;
        if (cycleReduction > 0 || boostRestore > 0) {
          for (const equippedId of this.runInventory.weapons) {
            this.weaponCd[equippedId] = Math.max(
              0,
              (this.weaponCd[equippedId] ?? 0) * (1 - cycleReduction),
            );
          }
          this.player.boostFuel = Math.min(
            1 + this.mods().boostBonus,
            this.player.boostFuel + boostRestore,
          );
          this.message = `TETHERWING CATCH — cycles advanced ${Math.round(cycleReduction * 100)}%, boost +${Math.round(boostRestore * 100)}%.`;
          this.telemetry("tetherwing_projectile_catch", {
            weaponId: shot.weaponId,
            cycleReduction,
            boostRestore,
          });
        }
        continue;
      }

      if (shot.friendly) {
        let hitEnemy: EnemyState | null = null;
        let hitFraction = Number.POSITIVE_INFINITY;
        for (const enemy of this.enemies) {
          if (enemy.hull <= 0) continue;
          if (shot.hitEnemyIds?.includes(enemy.id)) continue;
          const fraction = segmentCircleHitFraction(start, end, enemy, shot.radius + 1.6);
          if (fraction == null || fraction >= hitFraction) continue;
          hitEnemy = enemy;
          hitFraction = fraction;
        }
        if (hitEnemy) {
          shot.x = start.x + (end.x - start.x) * hitFraction;
          shot.y = start.y + (end.y - start.y) * hitFraction;
          this.detonateProjectile(shot, hitEnemy);
          this.damageEnemy(hitEnemy, shot.damage, shot.weaponId, {
            slot: shot.sourceSlot,
            isShrapnel: shot.isShrapnel,
            element: shot.element,
          });
          shot.hitEnemyIds = [...(shot.hitEnemyIds ?? []), hitEnemy.id];
          next.push(...this.createImpactFragments(shot));
          if (this.retargetRicochet(shot)) {
            next.push(shot);
          } else if ((shot.remainingPierces ?? 0) > 0) {
            shot.remainingPierces = (shot.remainingPierces ?? 0) - 1;
            shot.damage *= shot.pierceDamageRetention ?? 1;
            next.push(shot);
          }
        } else {
          next.push(shot);
        }
      } else {
        const playerHit = segmentCircleHitFraction(
          start,
          end,
          this.player,
          shot.radius + PLAYER_RADIUS_M,
        );
        if (this.player.invulnTimer <= 0 && playerHit != null) {
          shot.x = start.x + (end.x - start.x) * playerHit;
          shot.y = start.y + (end.y - start.y) * playerHit;
          const owner = this.enemies.find((e) => e.id === shot.ownerId);
          const playerShieldBefore = this.player.shield;
          this.damagePlayer(shot.damage, {
            x: shot.x,
            y: shot.y,
            role: owner?.role,
            elite: owner?.elite,
            isAce: owner?.isAce,
            isArenaGuardian: owner?.isArenaGuardian,
            attackKind: "projectile",
          });
          if (owner?.role === "leech") {
            const shieldDrained = Math.max(0, playerShieldBefore - this.player.shield);
            const restored = Math.min(
              owner.maxShield - owner.shield,
              shieldDrained * LEECH_SHIELD_RETURN_MULT,
            );
            if (restored > 0) {
              owner.shield += restored;
              this.message = `CAPACITOR LEECH — reclaimed ${Math.round(restored)} shield.`;
              this.telemetry("leech_shield_return", {
                enemyId: owner.id,
                shieldDrained,
                shieldRestored: restored,
              });
            }
          }
          if (owner?.eliteAffix === "vampiric") {
            owner.hull = Math.min(owner.maxHull, owner.hull + shot.damage * 0.35);
          }
          if (owner?.role === "sapper" && this.player.boostFuel > 0) {
            const drained = Math.min(this.player.boostFuel, SAPPER_BOOST_DRAIN);
            this.player.boostFuel -= drained;
            this.message = `FUEL SAPPER HIT — ${Math.round(drained * 100)}% boost reserve vented.`;
            this.telemetry("sapper_boost_drained", { enemyId: owner.id, amount: drained });
          }
        } else {
          next.push(shot);
        }
      }
    }
    this.projectiles = next.filter(
      (projectile) => !this.counterpulseClearedProjectileIds.has(projectile.id),
    );
    this.counterpulseClearedProjectileIds.clear();
    this.enemies = this.enemies.filter((e) => e.hull > 0);
  }

  private detonateProjectile(shot: ProjectileState, primary: EnemyState | null): void {
    const radius = shot.blastRadiusM ?? 0;
    if (radius <= 0) return;

    // Clear first so piercing/ricochet projectiles cannot detonate more than once.
    shot.blastRadiusM = 0;
    const damage = shot.damage * (shot.blastDamageRetention ?? 1);
    const pull = shot.implosionPullM ?? 0;
    const targets = this.enemies.filter((enemy) => {
      if (enemy.hull <= 0 || enemy.id === primary?.id) return false;
      return dist2(shot, enemy) <= radius;
    });

    if (shot.clearsHostileProjectilesOnBlast) {
      let cleared = 0;
      for (const projectile of this.projectiles) {
        if (projectile.friendly || projectile.id === shot.id || dist2(shot, projectile) > radius) {
          continue;
        }
        if (this.counterpulseClearedProjectileIds.has(projectile.id)) continue;
        this.counterpulseClearedProjectileIds.add(projectile.id);
        cleared += 1;
      }
      this.telemetry("ordnance_counterpulse", {
        weaponId: shot.weaponId,
        radius,
        projectilesCleared: cleared,
      });
    }

    for (const enemy of targets) {
      const dx = shot.x - enemy.x;
      const dy = shot.y - enemy.y;
      const distance = Math.hypot(dx, dy);
      this.damageEnemy(enemy, damage, shot.weaponId, {
        slot: shot.sourceSlot,
        isShrapnel: shot.isShrapnel,
        element: shot.element,
      });
      if (enemy.hull <= 0 || pull <= 0 || distance <= 0.01) continue;
      const displacement = Math.min(pull, Math.max(0, distance - 1.8));
      enemy.x += (dx / distance) * displacement;
      enemy.y += (dy / distance) * displacement;
    }

    this.pushAudio({
      type: "ordnance_blast",
      weaponId: shot.weaponId,
      x: shot.x,
      y: shot.y,
      radius,
      element: shot.element,
    });
    this.telemetry("ordnance_blast", {
      weaponId: shot.weaponId,
      radius,
      targets: targets.length + (primary ? 1 : 0),
    });
  }

  private createImpactFragments(shot: ProjectileState): ProjectileState[] {
    const count = shot.fragmentCount ?? 0;
    const speed = shot.fragmentSpeed ?? 0;
    if (count <= 0 || speed <= 0) return [];
    shot.fragmentCount = 0;
    const fragments: ProjectileState[] = [];
    const startAngle = Math.atan2(shot.vy, shot.vx);
    for (let index = 0; index < count; index += 1) {
      const angle = startAngle + (index / count) * Math.PI * 2;
      fragments.push({
        id: this.nextId++,
        ownerId: shot.ownerId,
        weaponId: shot.weaponId,
        x: shot.x,
        y: shot.y,
        altitude: this.player.altitude,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.55,
        damage: shot.damage * (shot.fragmentDamageRetention ?? 1),
        radius: 0.25,
        friendly: true,
        element: shot.element,
        homingTurnRate: 0,
        homingRangeM: 0,
        isShrapnel: true,
        sourceSlot: shot.sourceSlot,
        remainingPierces: 0,
        remainingRicochets: 0,
        blastRadiusM: 0,
        fragmentCount: 0,
        hitEnemyIds: [...(shot.hitEnemyIds ?? [])],
      });
    }
    this.telemetry("projectile_cascade", { weaponId: shot.weaponId, fragments: count });
    return fragments;
  }

  private retargetRicochet(shot: ProjectileState): boolean {
    if ((shot.remainingRicochets ?? 0) <= 0 || (shot.ricochetRangeM ?? 0) <= 0) return false;
    const hitIds = new Set(shot.hitEnemyIds ?? []);
    let target: EnemyState | null = null;
    let targetDistance = shot.ricochetRangeM ?? 0;
    for (const enemy of this.enemies) {
      if (enemy.hull <= 0 || hitIds.has(enemy.id)) continue;
      const distance = dist2(shot, enemy);
      if (distance > targetDistance) continue;
      target = enemy;
      targetDistance = distance;
    }
    if (!target) return false;

    const speed = Math.max(1, Math.hypot(shot.vx, shot.vy));
    const offset = vec2(target.x - shot.x, target.y - shot.y);
    const direction = len2(offset) > 0.01 ? normalize2(offset) : normalize2(vec2(shot.vx, shot.vy));
    shot.vx = direction.x * speed;
    shot.vy = direction.y * speed;
    shot.damage *= shot.ricochetDamageRetention ?? 1;
    shot.remainingRicochets = (shot.remainingRicochets ?? 0) - 1;
    this.telemetry("projectile_ricochet", {
      weaponId: shot.weaponId,
      targetId: target.id,
      remaining: shot.remainingRicochets,
    });
    return true;
  }

  private damageEnemy(
    enemy: EnemyState,
    damage: number,
    weaponId?: WeaponId,
    opts: {
      slot?: WeaponSlot | null;
      isShrapnel?: boolean;
      element?: ProjectileState["element"];
      skipPayload?: boolean;
    } = {},
  ): void {
    const wasAlive = enemy.hull > 0;
    if (!wasAlive) return;
    const slot = opts.slot ?? (weaponId ? WEAPONS[weaponId]?.slot : null) ?? null;
    const slotFx = slot && !opts.skipPayload ? this.slotModEffects(slot) : null;

    if (slotFx && !opts.skipPayload) {
      applyPayloadOnHit(enemy, slotFx);
    }

    const shieldBefore = enemy.shield;
    let againstShield = 0;
    let againstHull = damage;
    if (slotFx && !opts.skipPayload) {
      const scaled = scaleDamageForPayload(damage, shieldBefore, slotFx, enemy);
      againstShield = scaled.againstShield;
      againstHull = scaled.againstHull;
    } else if (enemy.shield > 0) {
      againstShield = Math.min(enemy.shield, damage);
      againstHull = damage - againstShield;
    }

    // Weapon-native shield specialization is additive to the ordinary hit
    // budget. Cap it at the shield that remains so the bonus can never spill
    // through a nearly depleted shield into hull.
    const shieldDamageMult = weaponId ? (WEAPONS[weaponId]?.shieldDamageMult ?? 1) : 1;
    if (shieldBefore > againstShield && shieldDamageMult > 1) {
      againstShield += Math.min(shieldBefore - againstShield, damage * (shieldDamageMult - 1));
    }

    if (againstShield > 0) {
      enemy.shield = Math.max(0, enemy.shield - againstShield);
      this.pushAudio({ type: "shield_hit", x: enemy.x, y: enemy.y });
      if (slotFx && slotFx.shieldLeechFraction > 0) {
        const restored = Math.min(
          this.player.maxShield - this.player.shield,
          againstShield * slotFx.shieldLeechFraction,
        );
        if (restored > 0) {
          this.player.shield += restored;
          this.telemetry("capacitor_tap_return", {
            enemyId: enemy.id,
            sourceSlot: slot ?? "unknown",
            shieldDamage: againstShield,
            shieldRestored: restored,
          });
        }
      }
    }
    if (againstHull > 0) {
      enemy.hull -= againstHull;
      this.pushAudio({ type: "hull_hit", x: enemy.x, y: enemy.y });
    }
    const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
    const shieldBreakCycleReduction = frame.enemyShieldBreakCycleReduction ?? 0;
    if (shieldBefore > 0 && enemy.shield <= 0 && shieldBreakCycleReduction > 0) {
      for (const equippedId of this.runInventory.weapons) {
        this.weaponCd[equippedId] = Math.max(
          0,
          (this.weaponCd[equippedId] ?? 0) * (1 - shieldBreakCycleReduction),
        );
      }
      this.message = `BREACHWING SURGE — weapon cycles advanced ${Math.round(shieldBreakCycleReduction * 100)}%.`;
      this.telemetry("breachwing_shield_break_cycle", {
        enemyId: enemy.id,
        reduction: shieldBreakCycleReduction,
      });
    }
    if (wasAlive && enemy.hull <= 0) {
      if ((frame.killBoostRestore ?? 0) > 0) {
        this.player.boostFuel = Math.min(
          1 + this.mods().boostBonus,
          this.player.boostFuel + (frame.killBoostRestore ?? 0),
        );
      }
      if (weaponId && WEAPONS[weaponId]?.slot === "primary") {
        this.primaryKillCount += 1;
      }
      if (slot && slotFx && slotFx.crossSlotCooldownReduction > 0) {
        for (const equippedId of this.runInventory.weapons) {
          if (WEAPONS[equippedId]?.slot === slot) continue;
          this.weaponCd[equippedId] = Math.max(
            0,
            (this.weaponCd[equippedId] ?? 0) * (1 - slotFx.crossSlotCooldownReduction),
          );
        }
        this.telemetry("crossfeed_relay", {
          sourceSlot: slot,
          reduction: slotFx.crossSlotCooldownReduction,
        });
      }
      if (enemy.eliteAffix === "volatile") {
        if (this.player.invulnTimer <= 0 && dist2(enemy, this.player) < 12) {
          this.damagePlayer(22, {
            x: enemy.x,
            y: enemy.y,
            role: enemy.role,
            elite: enemy.elite,
            isAce: enemy.isAce,
            isArenaGuardian: enemy.isArenaGuardian,
            attackKind: "volatile",
          });
        }
      }
      if (enemy.isAce) this.runAceKilled = true;
      this.recentKills += 1;
      if (enemy.role === "carrier") {
        for (let i = 0; i < 2; i += 1) {
          this.spawnChildScout(enemy.x + i * 3, enemy.y, enemy.elite);
        }
      }
      if (slotFx && !opts.isShrapnel && !opts.skipPayload) {
        this.spawnKillBurst(enemy, damage, slotFx, weaponId, opts.element ?? slotFx.primaryElement);
      }
      this.pushAudio({
        type: "enemy_death",
        elite: enemy.elite,
        x: enemy.x,
        y: enemy.y,
      });
      const kill = recordEnemyKill(this.runProgress, enemy.role, enemy.elite);
      const scoreResult = processEnemyKill({
        progress: this.runProgress,
        combo: this.comboState,
        breakdown: this.scoreBreakdown,
        killsByRole: this.killsByRole,
        role: enemy.role,
        elite: enemy.elite,
        x: enemy.x,
        y: enemy.y,
        tick: this.tick,
        overkill: isOverkill(damage, enemy.maxHull),
        tactician: isTacticianKill(this.player.shield),
        nextEventId: this.nextScoreEventId,
        nextPopupId: this.nextScorePopupId,
        eliteScoreMult: this.mods().eliteScoreMult,
      });
      this.nextScoreEventId = scoreResult.nextEventId;
      this.nextScorePopupId = scoreResult.nextPopupId;
      this.pushScoreFeed(scoreResult.feed);
      this.scorePopups.push(...scoreResult.popups);
      noteDebriefKill(this.debriefStats, this.simTime, this.comboState.comboMultiplier);
      if (scoreResult.feed.some((e) => e.kind === "triple_kill")) this.runHadTripleKill = true;
      if (scoreResult.feed.some((e) => e.kind === "double_kill")) this.runHadDoubleKill = true;
      if (scoreResult.feed.some((e) => e.kind === "tactician")) this.runTacticianCount += 1;
      this.runProgress.bestChain = Math.max(this.runProgress.bestChain, this.comboState.chainCount);
      this.noteDaily({ type: "kill", elite: enemy.elite, ace: enemy.isAce });
      this.noteDaily({ type: "chain", count: this.comboState.chainCount });
      if (this.bounty.targetId === enemy.id) {
        const bountyLevels = grantXp(this.runProgress, BOUNTY_XP);
        this.runProgress.score += BOUNTY_SCORE;
        this.scoreBreakdown.style += BOUNTY_SCORE;
        this.message = `Priority target down. +${BOUNTY_SCORE} bounty.`;
        clearBounty(this.bounty, this.simTime, this.seed, this.tick);
        if (bountyLevels > 0) this.handleLevelUp(bountyLevels);
      }
      if (kill.levelsGained > 0) this.handleLevelUp(kill.levelsGained);
      this.spawnLoot(enemy);
      let reclaimed = enemy.stolenSalvage ?? 0;
      if (enemy.role === "harvester" || enemy.role === "leviathan" || enemy.role === "null_crown") {
        for (const minion of this.enemies) {
          if (minion.bossMinionOwnerId !== enemy.id || minion.hull <= 0) continue;
          reclaimed += minion.stolenSalvage ?? 0;
          minion.stolenSalvage = 0;
          minion.hull = 0;
        }
      }
      if (reclaimed > 0) {
        this.drops.push({
          id: this.nextId++,
          x: enemy.x + 1.5,
          y: enemy.y,
          kind: "salvage",
          amount: reclaimed,
          payloadId: "reclaimed_salvage",
          life: 60,
        });
        const recoveryLabel =
          enemy.role === "harvester"
            ? "HARVESTER SCRAPPED"
            : enemy.role === "leviathan"
              ? "FOUNDRY SILENCED"
              : enemy.role === "marauder"
                ? "MARAUDER BROKEN"
                : "SCAVENGER BROKEN";
        this.message = `${recoveryLabel} — ${reclaimed} stolen scrap recovered.`;
        this.telemetry("salvage_reclaimed", { enemyId: enemy.id, amount: reclaimed });
      }
      if (enemy.isArenaGuardian) {
        this.clearBossArenaForGuardian(enemy);
      } else {
        const poi = this.pois.find(
          (p) => p.kind === "boss_arena" && !p.cleared && dist2(p, enemy) < p.radius + 10,
        );
        if (poi && enemy.elite) {
          this.clearBossArena(poi, { bonusFromNamedGuardian: false });
        }
      }
    }
  }

  private spawnKillBurst(
    enemy: EnemyState,
    killDamage: number,
    slotFx: SlotModEffects,
    weaponId?: WeaponId,
    element: ProjectileState["element"] = null,
  ): void {
    const spec = shrapnelSpecFromKill(slotFx, killDamage);
    if (!spec) return;
    const wid =
      weaponId ?? loadoutWeaponForSlot(this.runInventory.weapons, "primary") ?? "kinetic_scatter";
    const slot = WEAPONS[wid]?.slot ?? "primary";
    for (const dir of shrapnelDirections(spec.count, enemy.facing)) {
      this.projectiles.push({
        id: this.nextId++,
        ownerId: this.player.id,
        weaponId: wid,
        x: enemy.x,
        y: enemy.y,
        altitude: enemy.altitude,
        vx: dir.x * spec.speed,
        vy: dir.y * spec.speed,
        life: spec.life,
        damage: spec.damage,
        radius: spec.radius,
        friendly: true,
        element,
        homingTurnRate: 0,
        homingRangeM: 0,
        isShrapnel: true,
        sourceSlot: slot,
      });
    }
    this.pushAudio({
      type: "kill_burst",
      x: enemy.x,
      y: enemy.y,
      element,
    });
  }

  private spawnChildScout(x: number, y: number, elite: boolean): void {
    const role = ENEMY_ROLES.scout!;
    this.enemies.push({
      id: this.nextId++,
      role: "scout",
      x,
      y,
      altitude: this.player.altitude,
      vx: 0,
      vy: 0,
      facing: 0,
      hull: role.hull * (elite ? 1.5 : 1),
      maxHull: role.hull * (elite ? 1.5 : 1),
      shield: 0,
      maxShield: 0,
      fireCooldown: role.fireCooldown * this.earlyFireCooldownMult(),
      elite: false,
      aiTimer: 0,
      aiState: "orbit",
      spawnTier: spawnTierForLevel(this.runProgress.level),
      telegraphTimer: 0,
      burstCd: 0.6,
      eliteAffix: null,
      isAce: false,
      isArenaGuardian: false,
      phaseTimer: 0,
      burnTimer: 0,
      burnDps: 0,
      slowTimer: 0,
      slowFactor: 0,
      shredTimer: 0,
      hullShred: 0,
      ionTimer: 0,
      shieldWeakened: 0,
      shockTimer: 0,
    });
  }

  private spawnChildScavenger(owner: EnemyState, offsetX: number, offsetY: number): void {
    const beforeId = this.nextId;
    this.spawnEnemy("scavenger", false, {
      x: owner.x + offsetX,
      y: owner.y + offsetY,
      affix: null,
      silentSpawnAudio: true,
    });
    const minion = this.enemies.find((enemy) => enemy.id >= beforeId && enemy.role === "scavenger");
    if (minion) minion.bossMinionOwnerId = owner.id;
  }

  private spawnChildMarauder(owner: EnemyState, offsetX: number, offsetY: number): void {
    const beforeId = this.nextId;
    this.spawnEnemy("marauder", false, {
      x: owner.x + offsetX,
      y: owner.y + offsetY,
      affix: null,
      silentSpawnAudio: true,
    });
    const minion = this.enemies.find((enemy) => enemy.id >= beforeId && enemy.role === "marauder");
    if (minion) minion.bossMinionOwnerId = owner.id;
  }

  private spawnChildNullifier(owner: EnemyState, offsetX: number, offsetY: number): void {
    const beforeId = this.nextId;
    this.spawnEnemy("nullifier", false, {
      x: owner.x + offsetX,
      y: owner.y + offsetY,
      affix: null,
      silentSpawnAudio: true,
    });
    const minion = this.enemies.find((enemy) => enemy.id >= beforeId && enemy.role === "nullifier");
    if (minion) minion.bossMinionOwnerId = owner.id;
  }

  private damagePlayer(damage: number, source?: DamageSourceInput): void {
    let remaining = damage;
    const mods = this.mods();
    if (source) {
      this.hitVignette = {
        bearing: Math.atan2(source.y - this.player.y, source.x - this.player.x),
        intensity: 1,
      };
      this.hitVignetteTimer = 0.35;
    }
    const shieldBefore = this.player.shield;
    let armorAbsorbed = 0;
    if (this.player.armor > 0 && mods.armorAbsorbPct > 0) {
      const absorb = remaining * mods.armorAbsorbPct;
      const fromArmor = Math.min(this.player.armor, absorb);
      this.player.armor -= fromArmor;
      remaining -= fromArmor;
      armorAbsorbed = fromArmor;
    }
    // Player-local hits stay full volume (no world attenuation); pan via source when known.
    const hitPos =
      source !== undefined
        ? {
            x: this.player.x + (source.x - this.player.x) * 0.15,
            y: this.player.y + (source.y - this.player.y) * 0.15,
          }
        : { x: this.player.x, y: this.player.y };
    let shieldAbsorbed = 0;
    if (this.player.shield > 0) {
      const absorbed = Math.min(this.player.shield, remaining);
      this.player.shield -= absorbed;
      remaining -= absorbed;
      shieldAbsorbed = absorbed;
      this.pushAudio({ type: "shield_hit", x: hitPos.x, y: hitPos.y });
      if (shieldBefore > 0 && this.player.shield <= 0) {
        this.pushAudio({ type: "shield_break", x: hitPos.x, y: hitPos.y });
        const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
        const reduction = frame.shieldBreakCycleReduction ?? 0;
        if (reduction > 0 && this.shieldBreakCycleProcCd <= 0) {
          for (const weaponId of Object.keys(this.weaponCd) as WeaponId[]) {
            this.weaponCd[weaponId] = Math.max(0, (this.weaponCd[weaponId] ?? 0) * (1 - reduction));
          }
          this.shieldBreakCycleProcCd = frame.shieldBreakCycleProcCooldownS ?? 0;
          this.message = `BACKLASH DISCHARGE — weapon cycles advanced ${Math.round(reduction * 100)}%.`;
          this.telemetry("backlash_discharge", { reduction });
        }
      }
    }
    let hullDamage = 0;
    if (remaining > 0) {
      this.player.hull -= remaining;
      hullDamage = remaining;
      this.pushAudio({ type: "hull_hit", x: hitPos.x, y: hitPos.y });
      resetKillStreak(this.runProgress);
      resetComboOnDamage(this.comboState);
      this.recentHullDamage += remaining;
      this.noHullDamageS = 0;
    }
    this.lastDamageSource = buildLastDamageSource(
      this.player,
      damage,
      shieldAbsorbed,
      armorAbsorbed,
      hullDamage,
      source,
    );
    noteDebriefDamage(this.debriefStats, shieldAbsorbed, armorAbsorbed, hullDamage);
    this.player.shieldDelay = Math.max(
      0.5,
      SHIELD_REGEN_DELAY_S + this.mods().shieldRegenDelayBonus,
    );
    if (this.player.hull <= 0) this.killPlayer();
  }

  private saveLastRun(outcome: "extracted" | "dead"): void {
    this.banked.lastRun = {
      level: this.runProgress.level,
      score: this.runProgress.score,
      bestStreak: this.runProgress.bestKillStreak,
      skillNodes: this.runProgress.takenNodes.length,
      outcome,
    };
  }

  private finishRunMeta(outcome: "extracted" | "dead"): void {
    if (!this.runSummary) return;
    const rp = rankPointsForOutcome(outcome, this.runSummary.score, this.runSummary.level);
    applyRankPoints(this.banked.pilotMeta, rp);
    const pb = recordPersonalBest(
      this.banked.pilotMeta,
      this.runSummary.score,
      this.runSummary.grade,
    );
    for (const nodeId of this.runSummary.takenNodes) {
      const node = skillNodeById(nodeId);
      if (node) addBranchMastery(this.banked.pilotMeta, node.branch);
    }
    const eliteCores = this.runInventory.eliteCores;
    const unlocked = evaluateAchievements(this.banked.pilotMeta, this.runSummary, {
      totalKills: this.runSummary.totalKills,
      bestStreak: this.runSummary.bestStreak,
      bestChain: this.runSummary.bestChain,
      simTime: this.runSummary.simTime,
      salvageCollected: this.runSummary.salvageCollected,
      eliteCores,
      skillNodes: this.runSummary.takenNodes.length,
      runsCompleted: this.banked.runsCompleted,
      aceKilled: this.runAceKilled,
      incursionSurvived: this.runIncursionSurvived,
      roleCount: Object.keys(this.runSummary.killsByRole).filter(
        (k) =>
          (this.runSummary!.killsByRole[k as keyof typeof this.runSummary.killsByRole] ?? 0) > 0,
      ).length,
      hadTripleKill: this.runHadTripleKill,
      hadDoubleKill: this.runHadDoubleKill,
      tacticianCount: this.runTacticianCount,
      bossArenasCleared: this.runBossArenasCleared,
      longestNoHullDamageS: this.longestNoHullDamageS,
    });
    if (pb && !this.banked.pilotMeta.achievements.includes("personal_best")) {
      unlocked.push("personal_best");
    }
    for (const id of unlocked) {
      if (!this.banked.pilotMeta.achievements.includes(id)) {
        this.banked.pilotMeta.achievements.push(id);
      }
    }
    this.banked.runHistory.unshift({
      outcome,
      score: this.runSummary.score,
      level: this.runSummary.level,
      grade: this.runSummary.grade,
      simTime: this.runSummary.simTime,
      at: new Date().toISOString(),
    });
    this.banked.runHistory = this.banked.runHistory.slice(0, 10);
  }

  public rerollSkillPick(): boolean {
    if (!hasRerollUnlock(this.banked.pilotMeta)) return false;
    if (this.runProgress.skillPickOffers.length === 0) return false;
    if (this.runProgress.rerollsUsed >= rerollCharges(this.banked.pilotMeta)) return false;
    this.runProgress.rerollsUsed += 1;
    this.runProgress.skillPickOffers = [];
    this.queueSkillPickIfNeeded();
    this.message = "Skill offers rerolled.";
    return true;
  }

  public banishSkillPick(index: number): boolean {
    if (!hasBanishUnlock(this.banked.pilotMeta)) return false;
    const nodeId = this.runProgress.skillPickOffers[index];
    if (!nodeId || this.runProgress.banishedNodes.includes(nodeId)) return false;
    this.runProgress.banishedNodes.push(nodeId);
    this.runProgress.skillPickOffers = [];
    this.queueSkillPickIfNeeded();
    this.message = `Banished ${skillNodeById(nodeId)?.name ?? nodeId} this run.`;
    return true;
  }

  private killPlayer(): void {
    this.forfeitSkillPick();
    const deathReport = this.lastDamageSource;
    const contract = activeSortieContract(this.activeContractId);
    const retainedCargo = Math.round(
      this.runInventory.salvage * (contract?.deathSalvageRetention ?? 0),
    );
    const salvageRetained = scaleCareerSalvage(retainedCargo, this.banked.pilotMeta.rank);
    if (salvageRetained > 0) this.banked.salvage += salvageRetained;
    this.runSummary = buildRunSummary(
      "dead",
      this.runProgress,
      this.scoreBreakdown,
      this.comboState,
      this.killsByRole,
      this.simTime,
      this.runSalvageCollected,
      { deathReport, salvageBanked: salvageRetained, debrief: this.debriefStats },
    );
    this.saveLastRun("dead");
    this.banked.extractStreak = applyExtractFailure().streak;
    this.noteDaily({
      type: "outcome",
      extracted: false,
      grade: this.runSummary.grade,
      simTime: this.simTime,
    });
    this.finishRunMeta("dead");
    this.phase = "dead";
    this.banked.runsFailed += 1;
    const recoveryNote = salvageRetained > 0 ? ` Recovery Bond returned ${salvageRetained}.` : "";
    this.message = deathReport
      ? formatDeathMessage(deathReport, this.runSummary.grade)
      : `Hull critical — Grade ${this.runSummary.grade}. Review debrief below. Relaunch with R.`;
    this.message += recoveryNote;
    this.pushAudio({ type: "death" });
    this.telemetry("death", {
      salvageLost: this.runInventory.salvage - retainedCargo,
      salvageRetained,
      simTime: this.simTime,
      level: this.runProgress.level,
      score: this.runProgress.score,
      grade: this.runSummary.grade,
      killer: deathReport?.enemyName ?? "unknown",
      attack: deathReport?.attackLabel ?? "unknown",
      killDistance: deathReport ? Math.round(deathReport.distanceM) : -1,
      killDamage: deathReport ? Math.round(deathReport.damage) : 0,
    });
    this.runInventory = emptyInventory(this.banked.selectedWeapons);
    this.runProgress = createRunProgress();
    this.comboState = createComboState();
    this.scoreBreakdown = createScoreBreakdown();
    this.killsByRole = {};
    this.runSalvageCollected = 0;
    this.debriefStats = createRunDebriefStats();
  }

  private spawnLoot(enemy: EnemyState): void {
    const rng = createRng(hashSeed(this.seed, enemy.id * 97 + this.tick));
    const mods = this.mods();
    const salvage = Math.round(rngRange(rng, 4, 14) * (enemy.elite ? 3 : 1) * mods.salvageMult);
    this.drops.push({
      id: this.nextId++,
      x: enemy.x,
      y: enemy.y,
      kind: "salvage",
      amount: salvage,
      life: 45,
    });
    if (enemy.elite) {
      this.drops.push({
        id: this.nextId++,
        x: enemy.x + 2,
        y: enemy.y,
        kind: "elite_core",
        amount: 1,
        life: 60,
      });
    }
    if (rng() < 0.22) {
      this.drops.push({
        id: this.nextId++,
        x: enemy.x - 2,
        y: enemy.y + 1,
        kind: "weapon",
        amount: 1,
        payloadId: rollWeaponLoot(rng, this.banked.pilotMeta.rank),
        life: 40,
      });
    }
    if (rng() < AFFIX_DROP_CHANCE) {
      const affix = AFFIXES[Math.floor(rng() * AFFIXES.length)]!;
      this.drops.push({
        id: this.nextId++,
        x: enemy.x + 1,
        y: enemy.y - 2,
        kind: "affix",
        amount: 1,
        payloadId: affix.id,
        life: 40,
      });
    }
    if (rng() < 0.15) {
      const component = COMPONENTS[Math.floor(rng() * COMPONENTS.length)]!;
      this.drops.push({
        id: this.nextId++,
        x: enemy.x - 1,
        y: enemy.y - 1,
        kind: "component",
        amount: 1,
        payloadId: component.id,
        life: 40,
      });
    }
  }

  private updateDrops(dt: number): void {
    // Ensure a weapon crate early for onboarding / combat slice (aim-fire beat).
    if (
      (this.phase === "onboarding" || this.phase === "expedition") &&
      this.onboardingStep <= ONBOARDING_STEP.AIM_FIRE &&
      !this.drops.some((d) => d.kind === "weapon") &&
      !this.pickedOnboardingCrate &&
      this.simTime > 2
    ) {
      this.drops.push({
        id: this.nextId++,
        x: this.carrierX + 35,
        y: this.carrierY + 10,
        kind: "weapon",
        amount: 1,
        payloadId: ONBOARDING_WEAPON,
        life: 120,
      });
    }

    const kept: DropState[] = [];
    for (const drop of this.drops) {
      drop.life -= dt;
      if (drop.life <= 0) continue;
      if (dist2(drop, this.player) < PICKUP_RADIUS_M * this.mods().pickupRadiusMult) {
        this.collectDrop(drop);
        continue;
      }
      kept.push(drop);
    }
    this.drops = kept;
  }

  private collectDrop(drop: DropState): void {
    let pickupKind: "affix" | "salvage" | "weapon" | "component" | "elite_core" = "salvage";
    let pickupAffixId: string | undefined;
    let flashPayload: string | null = null;
    const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;

    if (drop.kind === "salvage") {
      const amount = Math.round(drop.amount * frame.cargoMult);
      this.recordCollectedSalvage(amount);
      pickupKind = "salvage";
      this.message = pickupAnnounce("salvage");
    } else if (drop.kind === "elite_core") {
      pickupKind = "elite_core";
      this.runInventory.eliteCores += 1;
      this.message = pickupAnnounce("elite_core");
    } else if (drop.kind === "weapon" && drop.payloadId) {
      pickupKind = "weapon";
      const id = drop.payloadId as WeaponId;
      const def = WEAPONS[id];
      if (def && this.runInventory.weapons.includes(id)) {
        const recycled = this.recordCollectedSalvage(
          Math.round(weaponRecycleSalvage(id) * (frame.recycleMult ?? 1)),
        );
        pickupKind = "salvage";
        this.message = `DUPLICATE ${def.name.toUpperCase()} — +${recycled} salvage recycled.`;
      } else if (def) {
        const withoutSlot = this.runInventory.weapons.filter((w) => WEAPONS[w]?.slot !== def.slot);
        this.runInventory.weapons = [...withoutSlot, id];
        // Bank unlock so findable weapons become craftable-loadout targets after the run.
        if (!this.banked.unlockedWeapons.includes(id)) {
          this.banked.unlockedWeapons = [...this.banked.unlockedWeapons, id];
        }
      } else if (!this.runInventory.weapons.includes(id)) {
        this.runInventory.weapons.push(id);
      }
      if (id === ONBOARDING_WEAPON || def?.slot === "ordnance") {
        this.pickedOnboardingCrate = true;
      }
      if (pickupKind === "weapon") {
        flashPayload = id;
        this.message = pickupAnnounce("weapon", id);
      }
    } else if (drop.kind === "affix" && drop.payloadId) {
      if (canAddAffix(this.runInventory.affixes, drop.payloadId)) {
        this.runInventory.affixes.push(drop.payloadId);
        this.recomputePlayerVitals(false);
        pickupKind = "affix";
        pickupAffixId = drop.payloadId;
        flashPayload = drop.payloadId;
        this.message = pickupAnnounce("affix", drop.payloadId);
      } else {
        const recycled = this.recordCollectedSalvage(Math.round(8 * (frame.recycleMult ?? 1)));
        pickupKind = "salvage";
        this.message = `${pickupAnnounce("affix", drop.payloadId, { incompatible: true })} +${recycled} recycled.`;
      }
    } else if (drop.kind === "component" && drop.payloadId) {
      if (
        !this.runInventory.components.includes(drop.payloadId) &&
        this.runInventory.components.length < 3
      ) {
        pickupKind = "component";
        this.runInventory.components.push(drop.payloadId);
        this.recomputePlayerVitals(true);
        flashPayload = drop.payloadId;
        this.message = pickupAnnounce("component", drop.payloadId);
      } else {
        const recycled = this.recordCollectedSalvage(Math.round(6 * (frame.recycleMult ?? 1)));
        pickupKind = "salvage";
        this.message = `${pickupAnnounce("component", drop.payloadId, { incompatible: true })} +${recycled} recycled.`;
      }
    }

    // Distinct ship flash for meaningful loot (affix / ordnance / component / elite).
    if (
      pickupKind === "affix" ||
      pickupKind === "weapon" ||
      pickupKind === "component" ||
      pickupKind === "elite_core"
    ) {
      this.pickupFlash = pickupKind === "affix" ? 0.72 : 0.58;
      this.pickupFlashAffixId = flashPayload;
      this.pickupFlashKind = pickupKind;
    }

    this.pushAudio({
      type: "pickup",
      kind: pickupKind,
      ...(pickupAffixId ? { affixId: pickupAffixId } : {}),
    });
    this.telemetry("loot_pickup", { kind: drop.kind });
  }

  /** Route loose and recycled salvage through one XP/score/debrief economy path. */
  private recordCollectedSalvage(amount: number): number {
    const contractMult = activeSortieContract(this.activeContractId)?.salvageMult ?? 1;
    const credited = Math.round(amount * contractMult);
    this.runInventory.salvage += credited;
    this.runSalvageCollected += credited;
    noteDebriefSalvage(this.debriefStats, this.simTime, this.runSalvageCollected);
    this.noteDaily({ type: "salvage", amount: credited });
    const frame = SHIP_FRAMES[this.player.frameId] ?? SHIP_FRAMES.skimmer!;
    const cycleReduction = frame.salvageOrdnanceCycleReduction ?? 0;
    const ordnanceId = loadoutWeaponForSlot(this.runInventory.weapons, "ordnance");
    if (ordnanceId && cycleReduction > 0) {
      this.weaponCd[ordnanceId] = Math.max(
        0,
        (this.weaponCd[ordnanceId] ?? 0) * (1 - cycleReduction),
      );
      this.telemetry("magpie_salvage_cycle", {
        weaponId: ordnanceId,
        reduction: cycleReduction,
      });
    }
    const xp = xpForSalvagePickup(credited);
    if (xp > 0) {
      const levels = grantXp(this.runProgress, xp);
      if (levels > 0) this.handleLevelUp(levels);
    }
    const salvageScore = scoreForSalvagePickup(
      this.runProgress,
      this.scoreBreakdown,
      credited,
      this.tick,
      this.nextScoreEventId,
    );
    if (salvageScore.feed) {
      this.nextScoreEventId += 1;
      this.pushScoreFeed([salvageScore.feed]);
    }
    return credited;
  }

  private recomputePlayerVitals(healShield: boolean): void {
    const frame = SHIP_FRAMES[this.player.frameId]!;
    const mods = this.mods();
    const maxHull = frame.hull + mods.hullBonus;
    const maxShield = (frame.shield + mods.shieldBonus) * mods.shieldMult;
    const maxArmor = Math.max(0, mods.armorBonus);
    const hullRatio = this.player.maxHull > 0 ? this.player.hull / this.player.maxHull : 1;
    const armorRatio = this.player.maxArmor > 0 ? this.player.armor / this.player.maxArmor : 1;
    this.player.maxHull = maxHull;
    this.player.maxShield = maxShield;
    this.player.maxArmor = maxArmor;
    this.player.hull = clamp(maxHull * hullRatio, 1, maxHull);
    this.player.armor = clamp(maxArmor * (healShield ? 1 : armorRatio), 0, maxArmor);
    if (healShield) {
      this.player.shield = Math.min(maxShield, this.player.shield + 10);
    } else {
      this.player.shield = Math.min(this.player.shield, maxShield);
    }
  }

  private updateWorldStreaming(): void {
    const chunks = this.streamer.ensureAround(this.player.x, this.player.y, ACTIVE_CHUNK_RADIUS);
    this.pois = materializePois(chunks, this.pois);
  }

  /** Entry tips + light one-shot payoffs per POI kind. */
  private updatePoiInteractions(): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding" && this.phase !== "extracting") {
      return;
    }
    for (const poi of this.pois) {
      if (poi.cleared) continue;
      const dist = dist2(this.player, poi);
      if (dist > poi.radius) continue;

      const def = poiDef(poi.kind);
      if (poi.kind === "boss_arena") {
        this.beginBossArenaEncounter(poi);
        continue;
      }

      this.message = def.tip;
      this.applyPoiPayoff(poi);
      poi.cleared = true;
      this.telemetry("poi_trigger", { kind: poi.kind, poiId: poi.id });
    }
  }

  /** Start Arena Guardian fight on first zone entry (visible spawn + banner + sting). */
  private beginBossArenaEncounter(poi: WorldSnapshot["pois"][number]): void {
    if (poi.cleared) return;

    if (!this.tippedPoiIds.has(poi.id)) {
      this.tippedPoiIds.add(poi.id);
    }

    if (this.engagedBossPoiIds.has(poi.id)) {
      // Re-entry while guardian still alive — keep objective loud.
      if (this.bossGuardianByPoi.has(poi.id) && this.bossBannerTimer <= 0) {
        this.message = BOSS_ARENA_OBJECTIVE;
        this.bossObjective = BOSS_ARENA_OBJECTIVE;
      }
      return;
    }

    this.engagedBossPoiIds.add(poi.id);

    const toPlayer = angleOf({
      x: this.player.x - poi.x,
      y: this.player.y - poi.y,
    });
    // Spawn inside the ring toward the player so the guardian reads on first contact.
    const spawnR = Math.min(12, poi.radius * 0.42);
    const gx = poi.x + Math.cos(toPlayer) * spawnR;
    const gy = poi.y + Math.sin(toPlayer) * spawnR;
    const beforeId = this.nextId;
    const bossRole: EnemyRoleId =
      this.banked.pilotMeta.rank >= 100
        ? "centurion"
        : this.banked.pilotMeta.rank >= 80
          ? "null_crown"
          : this.banked.pilotMeta.rank >= 60
            ? "leviathan"
            : this.banked.pilotMeta.rank >= 50
              ? "sovereign"
              : this.banked.pilotMeta.rank >= 36
                ? "harvester"
                : this.banked.pilotMeta.rank >= 22
                  ? "warden"
                  : "bomber";
    this.spawnEnemy(bossRole, true, {
      x: gx,
      y: gy,
      isArenaGuardian: true,
      silentSpawnAudio: true,
      affix:
        bossRole === "warden" ||
        bossRole === "harvester" ||
        bossRole === "sovereign" ||
        bossRole === "leviathan" ||
        bossRole === "null_crown" ||
        bossRole === "centurion"
          ? null
          : undefined,
    });
    const guardian = this.enemies.find((e) => e.id >= beforeId && e.isArenaGuardian);
    if (guardian) {
      this.bossGuardianByPoi.set(poi.id, guardian.id);
    }

    const { banner, objective } = bossPresentation(bossRole);
    this.bossBannerLabel = banner;
    this.bossBannerTone = "enter";
    this.bossBannerTimer = 2.6;
    this.bossObjective = objective;
    this.message = `${banner} — ${objective}`;
    this.bossAtmosphere = 1;
    this.pushAudio({ type: "boss_arena", phase: "enter", x: poi.x, y: poi.y });
    this.telemetry("boss_arena_enter", { poiId: poi.id, guardianId: guardian?.id ?? -1 });
  }

  private updateBossEncounter(dt: number): void {
    if (this.bossBannerTimer > 0) {
      this.bossBannerTimer = Math.max(0, this.bossBannerTimer - dt);
    }

    // Drop dead links if guardian somehow vanished without clear payoff.
    for (const [poiId, enemyId] of [...this.bossGuardianByPoi.entries()]) {
      const alive = this.enemies.some((e) => e.id === enemyId && e.hull > 0);
      if (!alive) this.bossGuardianByPoi.delete(poiId);
    }

    const guardianAlive = this.enemies.find((e) => e.isArenaGuardian && e.hull > 0);
    if (guardianAlive) {
      this.bossObjective = bossPresentation(guardianAlive.role).objective;
      if (this.bossBannerTimer <= 0 && this.bossBannerTone !== "clear") {
        this.message = this.bossObjective;
      }
    } else if (this.bossBannerTone !== "clear" || this.bossBannerTimer <= 0) {
      this.bossObjective = null;
    }

    let atmosphere = 0;
    for (const poi of this.pois) {
      if (poi.kind !== "boss_arena" || poi.cleared) continue;
      const d = dist2(this.player, poi);
      if (d < poi.radius + 18) {
        atmosphere = Math.max(atmosphere, 1 - d / (poi.radius + 18));
      }
      if (this.bossGuardianByPoi.has(poi.id)) {
        atmosphere = Math.max(atmosphere, 0.75);
      }
    }
    if (this.bossBannerTimer > 0) {
      atmosphere = Math.max(atmosphere, this.bossBannerTone === "enter" ? 1 : 0.55);
    }
    // Ease atmosphere toward target.
    const target = Math.min(1, atmosphere);
    this.bossAtmosphere += (target - this.bossAtmosphere) * Math.min(1, dt * 4);
    if (this.bossAtmosphere < 0.02 && target < 0.02) this.bossAtmosphere = 0;
  }

  private clearBossArenaForGuardian(enemy: EnemyState): void {
    let poi =
      this.pois.find((p) => {
        const gid = this.bossGuardianByPoi.get(p.id);
        return gid === enemy.id;
      }) ?? null;
    if (!poi) {
      poi =
        this.pois.find(
          (p) => p.kind === "boss_arena" && !p.cleared && dist2(p, enemy) < p.radius + 40,
        ) ?? null;
    }
    if (!poi) {
      // Guardian died far from any arena — still grant XP once via orphan clear.
      poi =
        this.pois.find(
          (p) => p.kind === "boss_arena" && !p.cleared && this.engagedBossPoiIds.has(p.id),
        ) ?? null;
    }
    if (poi && !poi.cleared) {
      this.clearBossArena(poi, {
        bonusFromNamedGuardian: true,
        bossRole: enemy.role,
      });
    }
  }

  private clearBossArena(
    poi: WorldSnapshot["pois"][number],
    opts: { bonusFromNamedGuardian: boolean; bossRole?: EnemyRoleId },
  ): void {
    if (poi.cleared) return;
    poi.cleared = true;
    this.bossGuardianByPoi.delete(poi.id);

    const bossRole = opts.bossRole ?? "bomber";
    const xpReward =
      bossRole === "centurion"
        ? CENTURION_XP
        : bossRole === "null_crown"
          ? NULL_CROWN_XP
          : bossRole === "leviathan"
            ? FOUNDRY_LEVIATHAN_XP
            : bossRole === "sovereign"
              ? VELOCITY_SOVEREIGN_XP
              : bossRole === "harvester"
                ? DREAD_HARVESTER_XP
                : bossRole === "warden"
                  ? SIEGE_WARDEN_XP
                  : BOSS_ARENA_XP;
    const bossLevels = grantXp(this.runProgress, xpReward);
    if (bossLevels > 0) this.handleLevelUp(bossLevels);

    const rng = createRng(hashSeed(this.seed, poi.id * 733 + this.tick));
    let salvageTotal = 0;
    const pileCount =
      bossRole === "centurion"
        ? 18
        : bossRole === "null_crown"
          ? 14
          : bossRole === "leviathan"
            ? 12
            : bossRole === "sovereign"
              ? 10
              : bossRole === "harvester"
                ? 8
                : bossRole === "warden"
                  ? 6
                  : opts.bonusFromNamedGuardian
                    ? 4
                    : 2;
    for (let i = 0; i < pileCount; i += 1) {
      const amount = Math.round(rngRange(rng, 10, 22));
      salvageTotal += amount;
      this.drops.push({
        id: this.nextId++,
        x: poi.x + rngRange(rng, -8, 8),
        y: poi.y + rngRange(rng, -8, 8),
        kind: "salvage",
        amount,
        life: 55,
      });
    }
    if (opts.bonusFromNamedGuardian && rng() < 0.45) {
      const affix = AFFIXES[Math.floor(rng() * AFFIXES.length)]!;
      this.drops.push({
        id: this.nextId++,
        x: poi.x + 3,
        y: poi.y - 2,
        kind: "affix",
        amount: 1,
        payloadId: affix.id,
        life: 50,
      });
    }

    this.runBossArenasCleared += 1;
    const { clearBanner, label: bossLabel } = bossPresentation(bossRole);
    this.bossBannerLabel = clearBanner;
    this.bossBannerTone = "clear";
    this.bossBannerTimer = 2.4;
    this.bossObjective = null;
    this.message = `${clearBanner} — ${bossLabel} down. +${xpReward} XP, ~${salvageTotal} salvage on the pad. Scoop the drops.`;
    this.pushAudio({ type: "boss_arena", phase: "clear", x: poi.x, y: poi.y });
    this.telemetry("boss_arena_clear", {
      poiId: poi.id,
      xp: xpReward,
      bossRole: opts.bossRole ?? "arena_guardian",
      salvage: salvageTotal,
    });
  }

  private applyPoiPayoff(poi: WorldSnapshot["pois"][number]): void {
    const rng = createRng(hashSeed(this.seed, poi.id * 911 + this.tick));
    switch (poi.kind) {
      case "scrap_field": {
        for (let i = 0; i < 3; i += 1) {
          this.drops.push({
            id: this.nextId++,
            x: poi.x + rngRange(rng, -6, 6),
            y: poi.y + rngRange(rng, -6, 6),
            kind: "salvage",
            amount: Math.round(rngRange(rng, 6, 14)),
            life: 50,
          });
        }
        break;
      }
      case "relay": {
        this.relayLullTimer = 12;
        this.directorCd = Math.max(this.directorCd, 8);
        this.drops.push({
          id: this.nextId++,
          x: poi.x,
          y: poi.y,
          kind: "salvage",
          amount: 8,
          life: 40,
        });
        break;
      }
      case "ambush": {
        this.spawnEnemy("scout", false, {
          x: poi.x + rngRange(rng, 8, 14),
          y: poi.y + rngRange(rng, -6, 6),
        });
        this.spawnEnemy("skirmisher", false, {
          x: poi.x - rngRange(rng, 8, 14),
          y: poi.y + rngRange(rng, -6, 6),
        });
        break;
      }
      case "cache": {
        this.drops.push({
          id: this.nextId++,
          x: poi.x,
          y: poi.y,
          kind: "salvage",
          amount: Math.round(rngRange(rng, 14, 28)),
          life: 55,
        });
        if (rng() < 0.55) {
          const affix = AFFIXES[Math.floor(rng() * AFFIXES.length)]!;
          this.drops.push({
            id: this.nextId++,
            x: poi.x + 2,
            y: poi.y - 1,
            kind: "affix",
            amount: 1,
            payloadId: affix.id,
            life: 50,
          });
        } else {
          this.drops.push({
            id: this.nextId++,
            x: poi.x - 2,
            y: poi.y + 1,
            kind: "weapon",
            amount: 1,
            payloadId: rollWeaponLoot(rng, this.banked.pilotMeta.rank),
            life: 50,
          });
        }
        break;
      }
      default:
        break;
    }
  }

  private carrierNavState(): WorldSnapshot["carrierNav"] {
    const airborne =
      this.phase === "expedition" ||
      this.phase === "onboarding" ||
      this.phase === "extracting" ||
      this.phase === "launching";
    if (!airborne) return null;
    const wantsExtract =
      this.runInventory.salvage > 0 ||
      this.runInventory.eliteCores > 0 ||
      this.onboardingStep >= ONBOARDING_STEP.DONE ||
      this.phase === "extracting";
    if (!wantsExtract) return null;
    const dx = this.carrierX - this.player.x;
    const dy = this.carrierY - this.player.y;
    const distanceM = Math.hypot(dx, dy);
    const bearing = angleOf({ x: dx, y: dy });
    return {
      active: true,
      distanceM,
      bearing,
      label: `CARRIER ${Math.round(distanceM)}m`,
    };
  }

  private nearestPoiHint(): WorldSnapshot["nearestPoi"] {
    let best: WorldSnapshot["nearestPoi"] = null;
    let bestScore = Infinity;
    for (const poi of this.pois) {
      if (poi.cleared) continue;
      const d = dist2(this.player, poi);
      const range = poi.kind === "boss_arena" ? BOSS_ARENA_HINT_RANGE_M : 72;
      if (d >= range) continue;
      // Prefer uncleared boss arenas when nearby so compass/tip points at them.
      const score = poi.kind === "boss_arena" ? d * 0.55 : d;
      if (score >= bestScore) continue;
      bestScore = score;
      best = {
        id: poi.id,
        kind: poi.kind,
        name: poiDisplayName(poi.kind),
        distanceM: d,
        bearing: angleOf({ x: poi.x - this.player.x, y: poi.y - this.player.y }),
      };
    }
    return best;
  }

  private updateExtract(dt: number): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding" && this.phase !== "extracting") {
      return;
    }
    const atCarrier =
      dist2(this.player, { x: this.carrierX, y: this.carrierY }) <= EXTRACT_RADIUS_M;
    const canExtractCargo =
      this.runInventory.salvage > 0 ||
      this.runInventory.eliteCores > 0 ||
      this.onboardingStep >= ONBOARDING_STEP.DONE;
    const contract = activeSortieContract(this.activeContractId);
    const contractComplete = contractBossObjectiveMet(contract, this.runBossArenasCleared);
    if (atCarrier && canExtractCargo && !contractComplete) {
      this.phase = "expedition";
      this.extractProgress = 0;
      const remaining = Math.max(
        0,
        (contract?.requiredBossArenas ?? 0) - this.runBossArenasCleared,
      );
      this.message = `${contract?.hudLabel ?? "CONTRACT"} INCOMPLETE — destroy ${remaining} arena guardian${remaining === 1 ? "" : "s"} before extraction.`;
      return;
    }
    if (atCarrier && canExtractCargo) {
      this.phase = "extracting";
      const mods = this.mods();
      const rate = (1 / EXTRACT_HOLD_S) * (1 + mods.extractBonus);
      this.extractProgress = Math.min(1, this.extractProgress + dt * rate);
      this.pushAudio({ type: "extract_tick" });
      if (this.extractProgress >= 1) this.completeExtract();
    } else if (this.phase === "extracting") {
      this.extractProgress = Math.max(0, this.extractProgress - dt * 0.5);
      if (this.extractProgress <= 0 && this.phase === "extracting") {
        this.phase =
          this.banked.runsCompleted === 0 && this.onboardingStep < ONBOARDING_STEP.DONE
            ? "onboarding"
            : "expedition";
      }
    }
  }

  private completeExtract(): void {
    const careerSalvage = scaleCareerSalvage(this.runInventory.salvage, this.banked.pilotMeta.rank);
    const heated = applyExtractSuccess(this.banked.extractStreak, careerSalvage);
    this.banked.extractStreak = heated.streak;
    this.banked.bestExtractStreak = Math.max(this.banked.bestExtractStreak, heated.streak);
    const salvageBanked = heated.salvage;
    this.runSummary = buildRunSummary(
      "extracted",
      this.runProgress,
      this.scoreBreakdown,
      this.comboState,
      this.killsByRole,
      this.simTime,
      this.runSalvageCollected,
      { salvageBanked, debrief: this.debriefStats },
    );
    this.phase = "extracted";
    this.banked.salvage += salvageBanked;
    this.banked.runsCompleted += 1;
    if (this.runInventory.eliteCores > 0 && !this.banked.blueprints.includes("elite_core_line")) {
      this.banked.blueprints.push("elite_core_line");
    }
    this.saveLastRun("extracted");
    this.noteDaily({
      type: "outcome",
      extracted: true,
      grade: this.runSummary.grade,
      simTime: this.simTime,
    });
    this.finishRunMeta("extracted");
    const heatNote = heated.bonus > 0 ? ` Heat ${heated.streak} +${heated.bonus}.` : "";
    this.message = `Extracted — Grade ${this.runSummary.grade}. +${salvageBanked} salvage banked.${heatNote} Press E to relaunch.`;
    this.pushAudio({ type: "extract_complete" });
    this.telemetry("extract", {
      salvage: salvageBanked,
      affixes: this.runInventory.affixes.length,
      level: this.runProgress.level,
      score: this.runProgress.score,
      grade: this.runSummary.grade,
    });
    this.runInventory = emptyInventory(this.banked.selectedWeapons);
    this.runProgress = createRunProgress();
    this.comboState = createComboState();
    this.scoreBreakdown = createScoreBreakdown();
    this.killsByRole = {};
    this.runSalvageCollected = 0;
    this.debriefStats = createRunDebriefStats();
    this.extractProgress = 0;
  }

  private updateProgression(dt: number): void {
    if (this.phase !== "expedition" && this.phase !== "onboarding" && this.phase !== "extracting") {
      return;
    }

    tickCombo(this.comboState, dt);
    this.noHullDamageS += dt;
    this.longestNoHullDamageS = Math.max(this.longestNoHullDamageS, this.noHullDamageS);
    this.scorePopups = this.scorePopups
      .map((popup) => ({ ...popup, life: popup.life - dt }))
      .filter((popup) => popup.life > 0);

    const levelBefore = this.runProgress.level;
    const timeResult = awardTimeSurvivedXp(this.runProgress, this.simTime);
    if (timeResult.intervals > 0) {
      const scoreResult = scoreForTimeSurvived(
        this.runProgress,
        this.scoreBreakdown,
        timeResult.intervals,
        this.tick,
        this.nextScoreEventId,
      );
      if (scoreResult.feed) {
        this.nextScoreEventId += 1;
        this.pushScoreFeed([scoreResult.feed]);
      }
    }
    if (this.runProgress.level > levelBefore) {
      this.handleLevelUp(this.runProgress.level - levelBefore);
    }
  }

  private handleLevelUp(levelsGained: number): void {
    this.player.invulnTimer = Math.max(this.player.invulnTimer, LEVEL_UP_INVULN_S);
    this.runProgress.levelUpPending = true;
    this.queueSkillPickIfNeeded();
    const points = this.runProgress.unspentSkillPoints;
    this.message =
      this.runProgress.skillPickOffers.length > 0
        ? `Level ${this.runProgress.level}! Choose a skill upgrade (1–3).`
        : `Level ${this.runProgress.level}! ${points} unspent skill point${points === 1 ? "" : "s"}.`;
    this.telemetry("level_up", {
      level: this.runProgress.level,
      levelsGained,
      score: this.runProgress.score,
    });
  }

  private queueSkillPickIfNeeded(): void {
    if (this.runProgress.unspentSkillPoints <= 0) {
      this.runProgress.skillPickOffers = [];
      this.runProgress.levelUpPending = false;
      return;
    }
    if (this.runProgress.skillPickOffers.length > 0) return;

    const banished = new Set(this.runProgress.banishedNodes);
    const skillEligible = eligibleSkillNodes(
      this.runProgress.takenNodes,
      MAX_SKILL_NODES_PER_RUN,
      this.runProgress.banishedNodes,
    );
    const modEligible = eligibleWeaponModOffers(
      this.runProgress.weaponMods,
      this.equippedWeaponSlots(),
      this.banked.pilotMeta.rank,
      this.banked.blueprints,
    ).filter((id) => !banished.has(id));

    if (skillEligible.length === 0 && modEligible.length === 0) {
      this.runProgress.unspentSkillPoints = 0;
      this.runProgress.levelUpPending = false;
      this.message = `Level ${this.runProgress.level} — upgrade cap reached this run.`;
      return;
    }

    const rng = createRng(hashSeed(this.seed, this.tick + this.runProgress.level * 31));
    const skillPool = skillEligible.map((n) => n.id);
    const modPool = [...modEligible];
    const offers: string[] = [];

    const hasKeystone = this.runProgress.takenNodes.some(
      (id) => skillNodeById(id)?.kind === "keystone",
    );
    const keystones = skillPool.filter((id) => skillNodeById(id)?.kind === "keystone");
    if (!hasKeystone && keystones.length > 0 && offers.length < SKILL_PICK_CHOICES) {
      const idx = Math.floor(rng() * keystones.length);
      offers.push(keystones[idx]!);
    }
    if (modPool.length > 0 && offers.length < SKILL_PICK_CHOICES && rng() < 0.4) {
      const idx = Math.floor(rng() * modPool.length);
      offers.push(modPool[idx]!);
      modPool.splice(idx, 1);
    }

    const mixed = [...skillPool, ...modPool];
    while (offers.length < SKILL_PICK_CHOICES && mixed.length > 0) {
      const idx = Math.floor(rng() * mixed.length);
      const pick = mixed[idx]!;
      mixed.splice(idx, 1);
      if (!offers.includes(pick)) offers.push(pick);
    }
    this.runProgress.skillPickOffers = offers;
  }

  private applySkillPick(offerId: string): boolean {
    if (!this.runProgress.skillPickOffers.includes(offerId)) return false;

    if (isWeaponModOffer(offerId)) {
      const parsed = parseWeaponModOffer(offerId);
      if (!parsed) return false;
      if (!applyWeaponModStack(this.runProgress.weaponMods, parsed.modId, parsed.slot)) {
        return false;
      }
      this.runProgress.unspentSkillPoints = Math.max(0, this.runProgress.unspentSkillPoints - 1);
      this.runProgress.skillPickOffers = [];
      this.runProgress.levelUpPending = false;
      const def = weaponModById(parsed.modId);
      const stacks =
        this.runProgress.weaponMods.find((m) => m.modId === parsed.modId && m.slot === parsed.slot)
          ?.stacks ?? 1;
      this.message = `Weapon mod: ${def?.name ?? parsed.modId} ×${stacks} on ${parsed.slot}`;
      addBranchMastery(this.banked.pilotMeta, "weapons");
      this.telemetry("weapon_mod_pick", {
        modId: parsed.modId,
        slot: parsed.slot,
        stacks,
      });
      this.queueSkillPickIfNeeded();
      return true;
    }

    if (!canAddSkillNode(this.runProgress.takenNodes, offerId, MAX_SKILL_NODES_PER_RUN)) {
      return false;
    }
    this.runProgress.takenNodes.push(offerId);
    this.runProgress.unspentSkillPoints = Math.max(0, this.runProgress.unspentSkillPoints - 1);
    this.runProgress.skillPickOffers = [];
    this.runProgress.levelUpPending = false;
    const node = skillNodeById(offerId);
    this.message = `Installed: ${node?.name ?? offerId}`;
    if (node) addBranchMastery(this.banked.pilotMeta, node.branch);
    this.recomputePlayerVitals(true);
    this.telemetry("skill_pick", { nodeId: offerId });
    this.queueSkillPickIfNeeded();
    return true;
  }

  private updateOnboarding(dt: number): void {
    if (
      this.phase !== "onboarding" &&
      !(this.phase === "expedition" && this.banked.runsCompleted === 0)
    ) {
      return;
    }

    // move → boost → aim-fire → Q/F soft gate → carrier pad = home
    if (
      this.onboardingStep === ONBOARDING_STEP.MOVE &&
      len2({ x: this.player.vx, y: this.player.vy }) > 5
    ) {
      this.onboardingStep = ONBOARDING_STEP.BOOST;
      this.message = onboardingMessage(ONBOARDING_STEP.BOOST);
    } else if (this.onboardingStep === ONBOARDING_STEP.BOOST && this.wasBoosting) {
      this.onboardingStep = ONBOARDING_STEP.AIM_FIRE;
      this.message = onboardingMessage(ONBOARDING_STEP.AIM_FIRE);
    } else if (
      this.onboardingStep === ONBOARDING_STEP.AIM_FIRE &&
      (this.primaryKillCount > 0 || this.pickedOnboardingCrate)
    ) {
      this.enterQfLiteracyBeat();
    } else if (this.onboardingStep === ONBOARDING_STEP.QF_LITERACY) {
      this.onboardingSoftGateTimer += dt;
      if (this.firedSecondaryOrOrdnance || this.onboardingSoftGateTimer >= ONBOARDING_SOFT_GATE_S) {
        this.onboardingStep = ONBOARDING_STEP.CARRIER_HOME;
        this.message = onboardingMessage(ONBOARDING_STEP.CARRIER_HOME);
        this.markWeaponLiteracyCoached();
      }
    } else if (
      this.onboardingStep === ONBOARDING_STEP.CARRIER_HOME &&
      (this.runInventory.eliteCores > 0 ||
        (this.runInventory.salvage > 0 &&
          dist2(this.player, { x: this.carrierX, y: this.carrierY }) <= EXTRACT_RADIUS_M))
    ) {
      this.onboardingStep = ONBOARDING_STEP.DONE;
      this.message = onboardingMessage(ONBOARDING_STEP.CARRIER_HOME);
    }
  }

  private enterQfLiteracyBeat(): void {
    this.onboardingStep = ONBOARDING_STEP.QF_LITERACY;
    this.onboardingSoftGateTimer = 0;
    this.message = onboardingMessage(ONBOARDING_STEP.QF_LITERACY);
    this.coachPulse = "arc_torp";
    this.telemetry("onboarding_qf_coach", {
      primaryKills: this.primaryKillCount,
      crate: this.pickedOnboardingCrate ? 1 : 0,
    });
  }

  private markWeaponLiteracyCoached(): void {
    if (this.banked.weaponLiteracyCoached) return;
    this.banked = { ...this.banked, weaponLiteracyCoached: true };
  }

  private regenShields(dt: number): void {
    if (this.player.shieldDelay > 0) {
      this.player.shieldDelay -= dt;
      return;
    }
    const jammed = this.isShieldJammed();
    const rate = SHIELD_REGEN_PER_S * (jammed ? JAMMER_REGEN_MULT : 1);
    this.player.shield = Math.min(this.player.maxShield, this.player.shield + rate * dt);
  }

  private isShieldJammed(): boolean {
    return this.enemies.some(
      (e) => e.role === "jammer" && e.hull > 0 && dist2(e, this.player) < JAMMER_RADIUS_M,
    );
  }

  private isGuidanceSuppressed(): boolean {
    return this.enemies.some(
      (enemy) =>
        (enemy.role === "nullifier" || enemy.role === "null_crown") &&
        enemy.hull > 0 &&
        dist2(enemy, this.player) < NULLIFIER_FIELD_RADIUS_M,
    );
  }

  private updateHitVignette(dt: number): void {
    if (!this.hitVignette || this.hitVignetteTimer <= 0) {
      this.hitVignette = null;
      return;
    }
    this.hitVignetteTimer -= dt;
    this.hitVignette.intensity = Math.min(1, Math.max(0, this.hitVignetteTimer / 0.35));
    if (this.hitVignetteTimer <= 0) this.hitVignette = null;
  }

  private pushScoreFeed(entries: ScoreFeedEntry[]): void {
    this.scoreFeed.push(...entries);
    while (this.scoreFeed.length > 5) this.scoreFeed.shift();
  }

  private pushAudio(event: AudioEvent): void {
    // Beds replace in-place so they never starve one-shots of the per-frame budget.
    if (event.type === "thrust" || event.type === "approach") {
      const idx = this.audioEvents.findIndex((e) => e.type === event.type);
      if (idx >= 0) {
        this.audioEvents[idx] = event;
        return;
      }
    }
    if (this.audioEvents.length < 12) this.audioEvents.push(event);
  }

  private telemetry(name: string, data?: TelemetryEvent["data"]): void {
    this.telemetryEvents.push({ name, tick: this.tick, data });
  }

  private snapshot(): WorldSnapshot {
    const chunks = this.streamer.ensureAround(this.player.x, this.player.y, ACTIVE_CHUNK_RADIUS);
    const area = this.areaPressure();
    return {
      tick: this.tick,
      simTime: this.simTime,
      seed: this.seed,
      phase: this.phase,
      paused: this.paused,
      player: { ...this.player },
      projectiles: this.projectiles.map((p) => ({
        ...p,
        altitude: this.player.altitude,
        hitEnemyIds: p.hitEnemyIds ? [...p.hitEnemyIds] : undefined,
      })),
      // Normalize at the presentation boundary too. This covers paused/frozen
      // snapshots and any legacy state that has not entered an update yet.
      enemies: this.enemies.map((e) => ({ ...e, altitude: this.player.altitude })),
      hazards: this.hazards.map((h) => ({ ...h })),
      telegraphs: this.telegraphs.map((t) => ({ ...t })),
      drops: this.drops.map((d) => ({ ...d })),
      pois: this.pois.map((p) => ({ ...p })),
      carrierX: this.carrierX,
      carrierY: this.carrierY,
      extractRadiusM: EXTRACT_RADIUS_M,
      carrierNav: this.carrierNavState(),
      nearestPoi: this.nearestPoiHint(),
      bossBanner:
        this.bossBannerTimer > 0
          ? {
              label: this.bossBannerLabel,
              timer: this.bossBannerTimer,
              tone: this.bossBannerTone,
            }
          : null,
      bossObjective: this.bossObjective,
      bossAtmosphere: this.bossAtmosphere,
      extractProgress: this.extractProgress,
      runInventory: {
        ...this.runInventory,
        weapons: [...this.runInventory.weapons],
        components: [...this.runInventory.components],
        affixes: [...this.runInventory.affixes],
      },
      runProgress: cloneRunProgress(this.runProgress),
      combo: { ...this.comboState, recentTags: [...this.comboState.recentTags] },
      scoreBreakdown: { ...this.scoreBreakdown },
      scoreFeed: this.scoreFeed.map((entry) => ({ ...entry })),
      scorePopups: this.scorePopups.map((popup) => ({ ...popup })),
      runSummary: this.runSummary
        ? {
            ...this.runSummary,
            breakdown: { ...this.runSummary.breakdown },
            takenNodes: [...this.runSummary.takenNodes],
            killsByRole: { ...this.runSummary.killsByRole },
            deathReport: this.runSummary.deathReport ? { ...this.runSummary.deathReport } : null,
            debrief: cloneRunDebriefStats(this.runSummary.debrief),
          }
        : null,
      activeSortieContract: this.activeContractId,
      bossArenasCleared: this.runBossArenasCleared,
      incursionActive: this.incursion.active,
      incursionLabel: this.incursion.waveId ? INCURSION_LABELS[this.incursion.waveId] : "",
      incursionTimer: this.incursion.activeTimer,
      bounty: bountySnapshot(this.bounty),
      directorPressure: this.directorPressure,
      onboardingStep: this.onboardingStep,
      coachPulse: this.coachPulse,
      assistTargetId: this.assistTarget?.id ?? null,
      softLockPulse: this.softLockPulseTimer > 0,
      weaponSlots: this.weaponHudSlots(),
      lastDamageSource: this.lastDamageSource ? { ...this.lastDamageSource } : null,
      threatBudget: Math.max(0, area.budgetMax - this.threatSpent),
      threatBudgetMax: area.budgetMax,
      threatGrade: threatGradeForLevel(this.runProgress.level),
      biome: area.chunk.biome,
      biomeName: area.biome.name,
      dangerTier: area.dangerTier,
      dangerLabel: dangerLabel(area.dangerTier),
      chunkHazard: area.chunk.hazard,
      activeChunks: chunks.map((c) => c.key),
      message: this.message,
      shieldJammed: this.isShieldJammed(),
      guidanceSuppressed: this.isGuidanceSuppressed(),
      hitVignette: this.hitVignette ? { ...this.hitVignette } : null,
      pickupFlash: this.pickupFlash,
      pickupFlashAffixId: this.pickupFlashAffixId,
      pickupFlashKind: this.pickupFlashKind,
      audioEvents: [...this.audioEvents],
      telemetryEvents: [...this.telemetryEvents],
      cosmeticsTrailId: this.cosmeticsTrailId,
      settings: { ...this.settings },
    };
  }

  /** Exposed for tests / debug. */
  public debugState() {
    return {
      carrierRadius: CARRIER_RADIUS_M,
      lastCommand: this.lastCommand,
      playerBaseHull: PLAYER_BASE_HULL,
      playerBaseShield: PLAYER_BASE_SHIELD,
    };
  }
}
